import * as THREE from "three";
import { bakeStaticLocal, buildWeaponMeshes, disposeDeep, partsFor, setCachedShown } from "./viewmodel.js";
import { RoundedBoxGeometry } from "three/examples/jsm/geometries/RoundedBoxGeometry.js";
import { mergeGeometries } from "three/examples/jsm/utils/BufferGeometryUtils.js";
import { SKIN_REGIONS, type SkinRegion } from "./skinLayout.js";
import { toonGradient } from "./mapTextures.js";
import { TILE_M, avatarMaps } from "./avatarTextures.js";
import { warmTexture } from "./renderer.js";
import { ZombieModel, zombieModelArmed } from "./zombieModel.js";

export interface AvatarCosmetics {
  bodyColor?: string;
  glowColor?: string;
  headgear?: string;
  /** Optional custom skin texture (data URL or image URL). */
  skinUrl?: string;
}

const textureLoader = new THREE.TextureLoader();

/**
 * Taper a part along Y: scale X by `ax` and Z by `az`, each interpolated from
 * the part's bottom face to its top face — and fix the NORMALS analytically
 * while doing it.
 *
 * This is the whole trick behind the 2026-07-31 `graphics` silhouette pass. A
 * humanoid is a stack of tapered volumes (shoulders wider than the waist, hips
 * wider than the waist, thigh thicker than the knee); the avatar was a stack of
 * constant-section boxes, which is precisely what reads as "Minecraft". Doing
 * it in the vertex buffer keeps everything the draw-call budget cares about
 * identical — same mesh count, same materials, same UVs, so `applyRegionUV`
 * skins still land (invariant 5).
 *
 * Normals matter: `computeVertexNormals()` is not an option here because
 * RoundedBoxGeometry is NON-INDEXED, so recomputing would facet every rounded
 * corner. Instead apply the inverse-transpose of the taper's Jacobian. For
 * X = x·a(y), Z = z·b(y) that works out to
 *     n' = normalize( nx/a,  ny − x·a'·nx/a − z·b'·nz/b,  nz/b )
 * with a', b' constant for a linear taper.
 */
function taperGeometry(
  geo: THREE.BufferGeometry, h: number,
  ax: readonly [number, number], az: readonly [number, number],
): void {
  const pos = geo.attributes.position, nor = geo.attributes.normal;
  const dax = (ax[1] - ax[0]) / h, daz = (az[1] - az[0]) / h;
  for (let i = 0; i < pos.count; i++) {
    const x = pos.getX(i), y = pos.getY(i), z = pos.getZ(i);
    const t = Math.min(1, Math.max(0, y / h + 0.5));
    const a = ax[0] + (ax[1] - ax[0]) * t;
    const b = az[0] + (az[1] - az[0]) * t;
    pos.setXYZ(i, x * a, y, z * b);
    const nx = nor.getX(i), ny = nor.getY(i), nz = nor.getZ(i);
    const mx = nx / a;
    const my = ny - (x * dax * nx) / a - (z * daz * nz) / b;
    const mz = nz / b;
    const len = Math.hypot(mx, my, mz) || 1;
    nor.setXYZ(i, mx / len, my / len, mz / len);
  }
  pos.needsUpdate = true;
  nor.needsUpdate = true;
}

/**
 * Bend a part's SQUARE cross-section into an ELLIPTICAL one — the second half
 * of the silhouette work, and the reason these stop reading as blocks even in
 * profile (2026-08-01, mission `graphics`).
 *
 * The taper above fixed the proportions but every part still had four flat
 * sides meeting at four corners: a tapered cube is still a cube. Human limbs
 * are round, a ribcage is an oval, a cranium is an ovoid. This maps each
 * vertex's (x,z) through the standard square→disc "elliptical grid mapping"
 *     X = u·√(1 − v²/2),  Z = v·√(1 − u²/2)      (u = x/hx, v = z/hz ∈ [−1,1])
 * blended by `s` (0 = untouched box, 1 = full ellipse). It is a pure vertex-
 * buffer edit like the taper: same mesh count, same materials, same UVs, so
 * custom skins still land (invariant 5) and the draw-call budget is untouched.
 *
 * Face CENTRES are fixed points of the map (u=0 ⇒ Z=±hz, v=0 ⇒ X=±hx), which
 * is what lets the face features, visor and accents keep their authored
 * positions — only the corners are pulled in.
 *
 * Normals are corrected analytically for the same reason as the taper
 * (RoundedBoxGeometry is non-indexed, so computeVertexNormals would facet every
 * rounded corner): with x' = hx·f(u,v), z' = hz·g(u,v) and y untouched, the
 * in-plane Jacobian is J = [[a,b],[c,d]] for a = f_u, b = hx·f_v/hz,
 * c = hz·g_u/hx, d = g_v, and n' = (J⁻¹)ᵀ·n gives
 *     n'_x = d·n_x − c·n_z,  n'_z = a·n_z − b·n_x     (÷det, dropped by the
 * following normalize). MUST run BEFORE `taperGeometry`, which assumes the
 * constant half-extents hx = w/2, hz = d/2 this function is written against.
 */
export function roundCrossSection(geo: THREE.BufferGeometry, w: number, d: number, s: number): void {
  if (s <= 0) return;
  const pos = geo.attributes.position, nor = geo.attributes.normal;
  const hx = w / 2, hz = d / 2;
  for (let i = 0; i < pos.count; i++) {
    const x = pos.getX(i), z = pos.getZ(i);
    const u = Math.max(-1, Math.min(1, x / hx));
    const v = Math.max(-1, Math.min(1, z / hz));
    const ru = Math.sqrt(Math.max(1e-6, 1 - (u * u) / 2));
    const rv = Math.sqrt(Math.max(1e-6, 1 - (v * v) / 2));
    const fu = 1 - s + s * rv;              // ∂f/∂u
    const fv = -s * u * v / (2 * rv);       // ∂f/∂v
    const gv = 1 - s + s * ru;              // ∂g/∂v
    const gu = -s * v * u / (2 * ru);       // ∂g/∂u
    pos.setX(i, hx * (u * (1 - s) + s * u * rv));
    pos.setZ(i, hz * (v * (1 - s) + s * v * ru));
    const nx = nor.getX(i), ny = nor.getY(i), nz = nor.getZ(i);
    const b = (hx * fv) / hz, c = (hz * gu) / hx;
    const mx = gv * nx - c * nz;
    const mz = fu * nz - b * nx;
    // n' = (J⁻¹)ᵀ·n = (1/det)·(mx, det·ny, mz). The 1/det scale is dropped by
    // the normalize below, but ONLY if y is scaled to match — leaving ny alone
    // tilts every normal that has a vertical component (i.e. the whole rounded
    // shoulder/cap region). agent/bench-avatar-shape.mjs differentiates the map
    // numerically and catches exactly this.
    const my = ny * (fu * gv - b * c);
    const len = Math.hypot(mx, my, mz) || 1;
    nor.setXYZ(i, mx / len, my / len, mz / len);
  }
  pos.needsUpdate = true;
  nor.needsUpdate = true;
}

/** Scale a part's per-face 0..1 UVs so the shared avatar textures tile at a
 *  believable world density instead of stretching one weave over a whole limb.
 *  One shared texture can't carry a per-part `repeat`, so the density lives in
 *  the UVs. Skipped for skinned parts — `applyRegionUV` owns those UVs. */
function tileUV(geo: THREE.BufferGeometry, k: number): void {
  const uv = geo.attributes.uv;
  for (let i = 0; i < uv.count; i++) uv.setXY(i, uv.getX(i) * k, uv.getY(i) * k);
  uv.needsUpdate = true;
}

type Taper = { x: readonly [number, number]; z: readonly [number, number] };

/* ---------------------------------------------------------------------------
 * COSMETIC GEAR toolkit (2026-08-01, mission `graphics`).
 *
 * The body and the held weapon are both PBR with elliptical sections since
 * earlier today; the worn cosmetics — cowboy hat/vest/holsters, pirate tricorn
 * and eyepatch, the hordebearer skull mask, the jetpack, the drop-in parachute
 * — were still flat-coloured axis-aligned BOXES, i.e. the crudest thing left on
 * the model. Two rules for the rebuild:
 *
 *  1. Every piece is real geometry (cocked brims, curved shells, wrapped
 *     panels, tanks with domed caps), not a slab standing in for one.
 *  2. Pieces that share a material are MERGED into one buffer, so a cosmetic
 *     that gained volumes does not gain draw calls — each of these is worn by
 *     every player on the map at once (the whole lobby wears the cowboy set on
 *     a Wild-West map), and a draw call here is per-avatar per-frame.
 * ------------------------------------------------------------------------- */

interface GearPiece {
  g: THREE.BufferGeometry;
  p?: readonly [number, number, number];
  r?: readonly [number, number, number];
  s?: readonly [number, number, number];
  /** UV tiling factor for the shared avatar textures (see `tileUV`). */
  tile?: number;
}

const _gearMat = new THREE.Matrix4();
const _gearEuler = new THREE.Euler();
const _gearQuat = new THREE.Quaternion();
const _gearPos = new THREE.Vector3();
const _gearScale = new THREE.Vector3();

/** Bake a piece list into ONE geometry. Sources are disposed; the result is
 *  owned by the caller (the gear groups dispose theirs on removal). */
function gearGeo(pieces: GearPiece[]): THREE.BufferGeometry {
  const out: THREE.BufferGeometry[] = [];
  for (const piece of pieces) {
    // mergeGeometries refuses a mix of indexed and non-indexed inputs, and
    // RoundedBoxGeometry is already non-indexed while the primitives are not.
    const g = piece.g.index ? piece.g.toNonIndexed() : piece.g;
    if (g !== piece.g) piece.g.dispose();
    if (piece.tile) tileUV(g, piece.tile);
    _gearPos.set(piece.p?.[0] ?? 0, piece.p?.[1] ?? 0, piece.p?.[2] ?? 0);
    _gearEuler.set(piece.r?.[0] ?? 0, piece.r?.[1] ?? 0, piece.r?.[2] ?? 0);
    _gearQuat.setFromEuler(_gearEuler);
    _gearScale.set(piece.s?.[0] ?? 1, piece.s?.[1] ?? 1, piece.s?.[2] ?? 1);
    g.applyMatrix4(_gearMat.compose(_gearPos, _gearQuat, _gearScale));
    // keep only what mergeGeometries can reconcile across primitive types
    for (const k of Object.keys(g.attributes)) {
      if (k !== "position" && k !== "normal" && k !== "uv") g.deleteAttribute(k);
    }
    out.push(g);
  }
  const merged = mergeGeometries(out) ?? out[0];
  for (const g of out) if (g !== merged) g.dispose();
  return merged;
}

/**
 * COCK A BRIM. Takes a flat disc (a short cylinder) and lifts it in `lobes`
 * evenly-spaced places, quadratically with radius, so the outer edge sweeps up
 * and the crown seat stays flat:
 *
 *     D(r,θ) = rise · max(0, cos(lobes·(θ − phase)))^2 · (r/R)²
 *
 * `lobes = 3` is a pirate TRICORN (the hat is literally named for the three
 * cocked corners); `lobes = 2` with `phase = 0` rolls the two SIDES up and
 * leaves front and back low, which is a cattleman cowboy brim. Both were flat
 * discs / flat boxes before, i.e. the two hats had the same silhouette as each
 * other and as a dinner plate.
 *
 * Normals are corrected analytically — for the height field (x, y+D, z) the
 * inverse-transpose gives n' = (nx − Dx·ny, ny, nz − Dz·ny). Same reason as
 * `taperGeometry`: these are non-indexed, so computeVertexNormals would facet
 * the whole brim.
 */
function cockBrim(
  geo: THREE.BufferGeometry, rOuter: number, lobes: number, rise: number, phase: number,
): void {
  const pos = geo.attributes.position, nor = geo.attributes.normal;
  const R2 = rOuter * rOuter;
  for (let i = 0; i < pos.count; i++) {
    const x = pos.getX(i), y = pos.getY(i), z = pos.getZ(i);
    const r = Math.hypot(x, z);
    if (r < 1e-5) continue;
    const th = Math.atan2(z, x);
    const c = Math.cos(lobes * (th - phase));
    if (c <= 0) continue;
    const w = c * c;                     // lobe weight
    const q = (r * r) / R2;              // radial ramp
    pos.setY(i, y + rise * w * q);
    // ∂D/∂r and ∂D/∂θ → cartesian gradient
    const dDdr = rise * w * (2 * r) / R2;
    const dDdt = rise * (-2 * c * Math.sin(lobes * (th - phase)) * lobes) * q;
    const ct = x / r, st = z / r;
    const gx = ct * dDdr - (st / r) * dDdt;
    const gz = st * dDdr + (ct / r) * dDdt;
    const nx = nor.getX(i), ny = nor.getY(i), nz = nor.getZ(i);
    const mx = nx - gx * ny, mz = nz - gz * ny;
    const len = Math.hypot(mx, ny, mz) || 1;
    nor.setXYZ(i, mx / len, ny / len, mz / len);
  }
  pos.needsUpdate = true;
  nor.needsUpdate = true;
}

/**
 * GORE a canopy: push a hemisphere's radius in and out `gores` times around Y
 * so the parachute reads as sewn fabric panels bulging between radial seams,
 * and scallop the skirt so the hem is not a laser-cut circle.
 *
 *     r' = r · (1 + amp·cos(gores·θ))
 *
 * Applied in the XZ plane only (the apex stays put). The scale is uniform per
 * ray, so the normal picks up only the tangential tilt: rotating the normal by
 * the surface slope d(amp)/dθ is a good enough approximation here and keeps the
 * seams reading as creases rather than facets.
 */
function goreCanopy(geo: THREE.BufferGeometry, gores: number, amp: number): void {
  const pos = geo.attributes.position, nor = geo.attributes.normal;
  for (let i = 0; i < pos.count; i++) {
    const x = pos.getX(i), z = pos.getZ(i);
    const r = Math.hypot(x, z);
    if (r < 1e-5) continue;
    const th = Math.atan2(z, x);
    const k = 1 + amp * Math.cos(gores * th);
    pos.setX(i, x * k);
    pos.setZ(i, z * k);
    // tangential slope of the radius modulation, in the horizontal plane
    const slope = (-amp * gores * Math.sin(gores * th)) / k;
    const ct = x / r, st = z / r;
    const nx = nor.getX(i), ny = nor.getY(i), nz = nor.getZ(i);
    // rotate the normal about Y by the seam slope: subtract the tangent term
    const mx = nx + slope * st * (nx * ct + nz * st);
    const mz = nz - slope * ct * (nx * ct + nz * st);
    const len = Math.hypot(mx, ny, mz) || 1;
    nor.setXYZ(i, mx / len, ny / len, mz / len);
  }
  pos.needsUpdate = true;
  nor.needsUpdate = true;
}

/* ---------------------------------------------------------------------------
 * THE HANDS + the arm rig (2026-08-01, mission `graphics`).
 *
 * Bone lengths are module constants because three separate things have to agree
 * on them: the constructor that builds the chain, `solveArm` (which needs the
 * two link lengths to close its triangle) and agent/bench-hands.mjs (which
 * transcribes the solver and asserts the hand actually lands on the grip).
 * ------------------------------------------------------------------------- */
/** Shoulder pivot height, and the two arm bones. Total reach from the shoulder
 *  to the GRIP POINT inside the fist is ARM_UPPER + ARM_LOWER. */
export const SHOULDER_Y = 1.31;
export const ARM_UPPER = 0.31;
export const ARM_FORE = 0.24;
/** Elbow → the point in the closed fist a weapon grip sits at. The hand mesh
 *  hangs off the wrist (at ARM_FORE) and the grip is inside the fingers. */
export const HAND_GRIP = 0.09;
export const ARM_LOWER = ARM_FORE + HAND_GRIP;
const ARM_W = 0.18;

/** Split a skin atlas region along its length, so a limb that is now TWO
 *  meshes still reads a drawn sleeve continuously instead of tiling it twice.
 *  `applyRegionUV` flips v, so t is measured from the TOP of the region. */
function subRegion(r: SkinRegion, t0: number, t1: number): SkinRegion {
  return { ...r, y: r.y + r.h * t0, h: r.h * (t1 - t0) };
}

/**
 * A closed FIST, as one merged geometry: palm, thumb wrapped across the front,
 * four curled fingers and a wrist cuff.
 *
 * Why merged: a hand is worn by every avatar in the room and a draw call here
 * is per-avatar per-frame, so the five volumes cost exactly one draw — the same
 * rule the cosmetic gear pass established. The old third-person fist used two
 * single bars for all fingers; at close range it still read as a mitten welded
 * to the arm. Individual curled finger volumes cost vertices but not draw
 * calls, and they give the hand a human scale cue beside the gun.
 *
 * Origin is the WRIST (y = 0), fingers curl toward -z (the model's front), and
 * `side` (+1 right, -1 left) mirrors the thumb.
 */
function handGeo(side: number): THREE.BufferGeometry {
  const rb = (w: number, h: number, d: number, round: number, r = 0.02): THREE.BufferGeometry => {
    const g = new RoundedBoxGeometry(w, h, d, 1, r) as unknown as THREE.BufferGeometry;
    roundCrossSection(g, w, d, round);
    return g;
  };
  // palm: broad across the knuckles, thin front-to-back, with the heel of the
  // hand a touch wider than the knuckle line
  const palm = rb(0.126, 0.134, 0.082, 0.6, 0.03);
  taperGeometry(palm, 0.134, [1.0, 0.92], [0.86, 1.0]);
  const pieces: GearPiece[] = [
    { g: palm, p: [0, -0.062, -0.004], tile: 1.5 },
    // THUMB: across the front of the grip, angled in and down. This is the
    // volume that makes a fist read as gripping something rather than as a
    // rounded brick — it breaks the silhouette on exactly the side the weapon
    // is on.
    { g: rb(0.045, 0.100, 0.052, 0.9, 0.017), p: [-side * 0.057, -0.061, -0.034], r: [-0.45, 0, side * 0.62], tile: 2.6 },
  ];
  for (const [x, len, drop] of [
    [-0.046, 0.064, 0.006],
    [-0.015, 0.075, 0.000],
    [0.016, 0.073, 0.001],
    [0.046, 0.058, 0.010],
  ] as const) {
    // proximal phalanx, curled forward off the knuckle
    pieces.push({ g: rb(0.025, len, 0.045, 0.78, 0.012), p: [x, -0.128 - drop, -0.032], r: [0.92, 0, 0], tile: 2.4 });
    // distal phalanx folding back under the grip; each finger remains distinct
    pieces.push({ g: rb(0.023, len * 0.72, 0.039, 0.82, 0.011), p: [x, -0.158 - drop, -0.071], r: [2.10, 0, 0], tile: 2.8 });
  }
  pieces.push(
    // wrist cuff — the glove's gauntlet, and it hides the wrist joint seam
    { g: rb(0.144, 0.040, 0.096, 0.85, 0.015), p: [0, 0.004, 0], tile: 1.8 },
  );
  return gearGeo(pieces);
}

/** Flexible undersuit sleeve that hides the mechanical elbow/wrist cuts.
 *  One merged mesh per arm, parented to the elbow pivot: the elbow cup overlaps
 *  the upper arm, the forearm wrap covers the lower bone, and the wrist gaiter
 *  tucks into the glove. This keeps the rig articulated for IK while no longer
 *  looking like separate puppet rods. */
function armJointSleeveGeo(): THREE.BufferGeometry {
  const rb = (w: number, h: number, d: number, round: number, r = 0.025): THREE.BufferGeometry => {
    const g = new RoundedBoxGeometry(w, h, d, 1, r) as unknown as THREE.BufferGeometry;
    roundCrossSection(g, w, d, round);
    return g;
  };
  return gearGeo([
    // elbow cup, straddling the hinge and overlapping both bones
    { g: rb(ARM_W * 1.18, 0.120, ARM_W * 1.20, 1, 0.040), p: [0, 0.010, 0], tile: 2.1 },
    // flexible sleeve over the forearm; dark fabric makes the bend read as a
    // suit joint, not a visible gap between two animated sticks
    { g: rb(ARM_W * 0.98, ARM_FORE * 0.74, ARM_W, 1, 0.030), p: [0, -ARM_FORE * 0.42, 0], tile: 2.4 },
    // raised accordion bands, merged into the same draw
    { g: new THREE.TorusGeometry(ARM_W * 0.50, 0.012, 6, 14), p: [0, -0.050, 0], r: [Math.PI / 2, 0, 0], s: [1.02, 1.0, 0.90], tile: 3 },
    { g: new THREE.TorusGeometry(ARM_W * 0.46, 0.010, 6, 14), p: [0, -0.125, 0], r: [Math.PI / 2, 0, 0], s: [1.02, 1.0, 0.90], tile: 3 },
    // wrist gaiter: deliberately overlaps the glove cuff
    { g: rb(ARM_W * 0.96, 0.085, ARM_W, 0.95, 0.024), p: [0, -ARM_FORE - 0.010, 0], tile: 2.8 },
  ]);
}

/** Leg equivalent of armJointSleeveGeo(): knee compression pad + calf sleeve +
 *  ankle gaiter, merged per leg. It hides the hard thigh/shin/boot boundaries
 *  that made the lower body read as a puppet even though the animation bends. */
function legJointSleeveGeo(w: number, shin: number): THREE.BufferGeometry {
  const rb = (bw: number, bh: number, bd: number, round: number, r = 0.03): THREE.BufferGeometry => {
    const g = new RoundedBoxGeometry(bw, bh, bd, 1, r) as unknown as THREE.BufferGeometry;
    roundCrossSection(g, bw, bd, round);
    return g;
  };
  return gearGeo([
    { g: rb(w * 1.22, 0.120, w * 1.18, 1, 0.040), p: [0, 0.018, -0.006], tile: 2.0 },
    { g: rb(w * 1.00, shin * 0.62, w * 0.98, 1, 0.034), p: [0, -shin * 0.34, 0], tile: 2.4 },
    { g: new THREE.TorusGeometry(w * 0.50, 0.014, 6, 14), p: [0, -0.060, 0], r: [Math.PI / 2, 0, 0], s: [1.05, 1.0, 0.92], tile: 3 },
    { g: new THREE.TorusGeometry(w * 0.47, 0.012, 6, 14), p: [0, -shin * 0.55, 0], r: [Math.PI / 2, 0, 0], s: [1.05, 1.0, 0.92], tile: 3 },
    { g: rb(w * 1.08, 0.095, w * 1.02, 0.95, 0.026), p: [0, -shin - 0.010, -w * 0.04], tile: 2.8 },
  ]);
}

/** One torso under-suit mesh: clavicle gasket, abdominal wrap, hip flex panels
 *  and side obliques. These are the visual glue between chest, waist, neck and
 *  legs; without them the hard body-part boundaries read like a mannequin. */
function torsoContinuityGeo(): THREE.BufferGeometry {
  const rb = (w: number, h: number, d: number, round: number, r = 0.03): THREE.BufferGeometry => {
    const g = new RoundedBoxGeometry(w, h, d, 1, r) as unknown as THREE.BufferGeometry;
    roundCrossSection(g, w, d, round);
    return g;
  };
  return gearGeo([
    // soft collar under the helmet/neck
    { g: rb(0.36, 0.090, 0.30, 0.95, 0.028), p: [0, 1.365, -0.010], tile: 2.2 },
    // sternum/abdomen compression panel spanning chest -> pelvis
    { g: rb(0.31, 0.330, 0.065, 0.62, 0.022), p: [0, 1.000, -0.195], tile: 2.0 },
    // waist belt/gasket across the old chest-pelvis cut
    { g: rb(0.54, 0.105, 0.34, 0.85, 0.032), p: [0, 0.940, -0.002], tile: 2.7 },
    // left/right oblique straps: they make the side profile continuous
    { g: rb(0.080, 0.300, 0.26, 0.85, 0.026), p: [-0.265, 1.020, -0.010], r: [0, 0, -0.12], tile: 2.4 },
    { g: rb(0.080, 0.300, 0.26, 0.85, 0.026), p: [0.265, 1.020, -0.010], r: [0, 0, 0.12], tile: 2.4 },
    // hip flex panels over the leg sockets
    { g: rb(0.165, 0.120, 0.23, 0.88, 0.030), p: [-0.175, 0.755, -0.025], r: [0.08, 0, -0.18], tile: 2.4 },
    { g: rb(0.165, 0.120, 0.23, 0.88, 0.030), p: [0.175, 0.755, -0.025], r: [0.08, 0, 0.18], tile: 2.4 },
  ]);
}

/* Scratch for the IK solve — hoisted, invariant 6 (no per-frame allocation). */
const _ikDir = new THREE.Vector3();
const _ikTarget = new THREE.Vector3();
const _ikGrip = new THREE.Vector3();
const _ikSeg = new THREE.Vector3();
const _ikRel = new THREE.Vector3();
const _ikQ = new THREE.Quaternion();
const _ikQRoll = new THREE.Quaternion();
const _ikQBend = new THREE.Quaternion();
const _IK_DOWN = new THREE.Vector3(0, -1, 0);
const _IK_X = new THREE.Vector3(1, 0, 0);
/** Where the held weapon sits in body space (the anchor the hands solve onto),
 *  and the grip point inside its own local frame. */
/** The weapon rides just right of the sternum, not out at arm's length on the
 *  right hip. Both numbers are dictated by REACH: the shoulders are ±0.375 and
 *  an arm is 0.64 long, so a gun parked at x = 0.38 (where it was) is a place
 *  the LEFT hand physically cannot get to — which is exactly why the old pose
 *  had to fake it with a fixed offset and eyeballed shoulder angles. */
export const GUN_X = 0.06, GUN_Y = 1.26, GUN_Z = -0.46;
export const GRIP_LOCAL_Y = -0.09, GRIP_LOCAL_Z = 0.04;

/**
 * Low-poly humanoid: head, torso, two arms, two legs. Legs + arms swing with
 * a walk cycle driven by horizontal speed. The torso front accepts a custom
 * skin texture (see the skin editor). Cosmetic-tinted otherwise.
 */
/** Half the stance width: the model's hips sit at x = ±0.175 (see makeLeg), so
 *  this is where a foot actually is when it plants. Exported so the decal
 *  system can put the print under the boot instead of under the navel. */
export const FOOT_HALF_STANCE = 0.175;

export class Avatar {
  readonly group = new THREE.Group();
  private legL: THREE.Object3D;
  private legR: THREE.Object3D;
  /** Knee pivots (children of the hip) — bent for a real crouch/kneel pose. */
  private kneeL: THREE.Object3D | null = null;
  private kneeR: THREE.Object3D | null = null;
  /** Ankle pivots (children of the knee) — the boot rolls heel→toe with gait. */
  private ankleL: THREE.Object3D | null = null;
  private ankleR: THREE.Object3D | null = null;
  private crouchT = 0; // eased 0→1 crouch amount
  /** Wall time of the previous update(), for frame-rate-independent easing. */
  private crouchLastMs = 0;
  private armL: THREE.Object3D;
  private armR: THREE.Object3D;
  /** Elbow pivots (children of the shoulder) — the arm is a two-bone chain and
   *  `solveArm` closes it on the weapon's grips. */
  private elbowL!: THREE.Object3D;
  private elbowR!: THREE.Object3D;
  /** Firing kick, 1 → 0. Drives the held weapon; the hands follow it through
   *  the IK, because they are solved onto the gun rather than posed near it. */
  private recoilT = 0;
  private head!: THREE.Object3D;
  private chest!: THREE.Object3D;
  private waist!: THREE.Object3D;
  private walkPhase = 0;
  private strideT = 0;
  /** AIRBORNE/LANDING (2026-08-08, realism pass): vertical velocity is derived
   *  from the y the caller already provides, so local, remote AND bot avatars
   *  all get the jump/fall pose with no signature change. `airT` eases 0→1
   *  airborne, `landK` is the landing-compression impulse. */
  private airT = 0;
  private airWas = false;
  private vyS = 0;
  private fallPeak = 0;
  private landK = 0;
  /** Local-space smoothed velocity (m/s): fwd = toward facing, lat = to the
   *  model's right — drives strafe lean and backpedal lean. */
  private fwdS = 0;
  private latS = 0;
  /** Death collapse targets, picked once in die() for per-death variety. */
  private deathPitch = 0;
  private deathRoll = Math.PI / 2;
  private deathYawDrift = 0;
  /** Footfall detection, consumed by `takeFootfall()`. `footHalf` is the index
   *  of the half-cycle the gait is currently in (see update()); `footPending` is
   *  the foot that planted since the last read: 0 none, -1 left, +1 right. */
  private footHalf = 0;
  private footHalfInit = false;
  private footPending = 0;
  private poseBobY = 0;
  private poseRotX = 0;
  private poseRotZ = 0;
  private zombie = false;
  /** PURGE team play: dead body stays visible for the revive interaction. */
  corpsePersist = false;
  /** ZOMBIE look: arms stretched straight forward, no weapon, shamble sway. */
  private zombieEyes: THREE.Mesh[] = [];
  setZombie(on: boolean): void {
    if (this.zombie === on) {
      // armed after this avatar became a zombie (map-change cinematic path):
      // attach on the next visual pass — still under the load screen
      if (on && !this.zm && zombieModelArmed()) this.syncZombieModel();
      return;
    }
    this.zombie = on;
    if (on && this.heldWeapon) { setCachedShown(this.heldWeapon, false); }
    else if (this.heldWeapon) setCachedShown(this.heldWeapon, true);
    if (on && this.zombieEyes.length === 0) {
      // hollow RED GLOWING EYES on the face — reads unmistakably undead
      const mat = new THREE.MeshBasicMaterial({ color: "#ff2211" });
      for (const ex of [-0.08, 0.08]) {
        const eye = new THREE.Mesh(new THREE.BoxGeometry(0.07, 0.05, 0.02), mat);
        eye.position.set(ex, 0.03, -0.165);
        this.head.add(eye);
        this.zombieEyes.push(eye);
      }
    }
    for (const e of this.zombieEyes) e.visible = on;
    this.applyCosmeticDetail();
    this.syncZombieModel();
  }

  // ---- REALISTIC ZOMBIE BODY (zombieModel.ts, 2026-10-23) -------------------
  // While a zombie and the GLB is armed, every procedural child (body parts,
  // eyes, gear, cached weapons) is parked in a hidden, matrix-frozen stash and
  // ONE skinned model stands in; the nameplate and the overhead markers stay.
  // The stash keeps the hierarchy intact for the human path (and for
  // agent/bench-hands.mjs, which reads group.children) — leaving zombie mode
  // moves everything back exactly as it was.
  private zm: ZombieModel | null = null;
  private zmStash: THREE.Group | null = null;
  private zmTint = "";
  private zmKids = -1;
  private zmAttack = false;
  private zmSpeed = 0;
  private zmLastMs = 0;
  /** Claw-range cue from the match loop (no swing event is replicated). */
  setZombieAttack(on: boolean): void { this.zmAttack = on; }
  /** The realistic body is live on this avatar. */
  get hasZombieModel(): boolean { return this.zm !== null; }
  private syncZombieModel(): void {
    const want = this.zombie && zombieModelArmed();
    if (want && !this.zm) {
      this.zm = new ZombieModel(this.zmTint);
      const stash = new THREE.Group();
      stash.name = "npZombieStash";
      setCachedShown(stash, false); // hidden AND out of the matrix pass
      this.zmStash = stash;
      this.group.add(stash, this.zm.root);
      this.stashProcedural();
      if (this.dying) this.zm.startDeath();
    } else if (!want && this.zm) {
      const stash = this.zmStash!;
      for (let i = stash.children.length - 1; i >= 0; i--) this.group.add(stash.children[i]);
      stash.removeFromParent();
      this.zmStash = null;
      this.zm.dispose();
      this.zm = null;
      this.zmKids = -1;
    }
  }
  /** Move every procedural child into the stash. Re-run whenever the group's
   *  child count changes (gear/weapons are added lazily). */
  private stashProcedural(): void {
    const stash = this.zmStash!, kids = this.group.children;
    for (let i = kids.length - 1; i >= 0; i--) {
      const c = kids[i];
      if (c === stash || c === this.zm!.root || c === this.plate || c === this.aaMark || c === this.fuseMark) continue;
      stash.add(c);
    }
    this.zmKids = kids.length;
  }
  /** update() for the realistic body: transform + one mixer step. */
  private updateZombieModel(x: number, y: number, z: number, yaw: number): void {
    const now = performance.now();
    const dt = this.zmLastMs > 0 ? Math.min(0.1, (now - this.zmLastMs) / 1000) : 1 / 60;
    this.zmLastMs = now;
    if (this.group.children.length !== this.zmKids) this.stashProcedural();
    this.group.position.set(x, y, z);
    this.group.rotation.set(0, yaw, 0);
    this.group.scale.set(1, 1, 1);
    // glide() mirrors these — the realistic body has no procedural bob/lean
    this.poseBobY = 0; this.poseRotX = 0; this.poseRotZ = 0; this.crouchT = 0;
    if (this.dying) {
      this.deathT += dt;
      this.deathLast = now;
      this.zm!.update(dt, 0, false, this.farLite);
      // the server removes a dead zombie ~1.6 s after the kill; this is only
      // the backstop for a body that somehow outlives that
      if (!this.corpsePersist && this.deathT > 4) this.group.visible = false;
      return;
    }
    let speed = 0;
    if (this.hasLast) {
      const dx = x - this.lastPos.x, dz = z - this.lastPos.z;
      speed = Math.sqrt(dx * dx + dz * dz) / Math.max(dt, 1 / 240);
      if (speed > 30) speed = 0; // teleport / respawn snap
    }
    this.lastPos.set(x, y, z);
    this.hasLast = true;
    this.zmSpeed += (speed - this.zmSpeed) * (1 - Math.exp(-dt * 7));
    this.zm!.update(dt, this.zmSpeed, this.zmAttack, this.farLite);
  }
  /** FAR-LOD draw-call diet. The npFX cosmetic draws per avatar (helmet dome,
   *  reactor core + glow, pauldrons, face features) read as sub-pixel noise past
   *  ~48 m — but a full 8-bot match rendered ALL of them at every range, and
   *  this class of draw was what a 2026 audit blamed for "5 AI = lag". The npOL
   *  half of the diet (the inverted-hull outlines and the glowing wireframe
   *  chest box) no longer exists at all as of 2026-08-01: those were the last
   *  comic-book tells on a now physically-shaded body, and deleting them
   *  measured 47 → 37 draw calls on the one-avatar cosmetics scene
   *  (agent/shot.mjs reads window.npPreviewInfo). The name check below is kept
   *  because it is free and other gear may want it back.
   *  LITE mode already hid them for zombies (see setZombie); this
   *  extends the same, proven toggle to any DISTANT remote, hero or undead.
   *  Body/skin/nameplate stay — only the flair drops. Collider-agnostic
   *  (invariant 5: these are visual-only children, the headshot AABB is
   *  server-side), so this can never affect hitreg. */
  private farLite = false;
  /** Last APPLIED hide state, so the traverse runs only on a real transition —
   *  a per-frame setFarLod() call with no change costs one boolean compare. */
  private cosmeticsHidden = false;
  setFarLod(on: boolean): void {
    if (this.farLite === on) return;
    this.farLite = on;
    this.applyCosmeticDetail();
  }
  isFarLod(): boolean { return this.farLite; }
  /** Hide the cosmetic flair whenever EITHER lever wants it off (a near zombie
   *  stays lite; a far hero goes lite), traversing only when the result flips. */
  private applyCosmeticDetail(): void {
    const hide = this.zombie || this.farLite;
    if (hide === this.cosmeticsHidden) return;
    this.cosmeticsHidden = hide;
    this.group.traverse((o) => {
      if (o.name === "npOL" || o.name === "npFX") o.visible = !hide;
    });
  }
  private lastPos = new THREE.Vector3();
  private hasLast = false;
  /** Held weapon mesh (in the right hand) + its world-space muzzle offset. */
  private heldWeapon: THREE.Group | null = null;
  private weaponId = "";
  /** Muzzle position in avatar-local space (for remote tracer origin). */
  readonly muzzleLocal = new THREE.Vector3(0.42, 1.15, -0.7);
  // floating nameplate + health bar so enemies are easy to spot
  private plate: THREE.Sprite | null = null;
  private plateCanvas: HTMLCanvasElement | null = null;
  private plateTex: THREE.CanvasTexture | null = null;
  private plateName = "";
  private plateHp = 1;
  private plateXp = 0;
  private dying = false;
  private deathT = 0;
  private deathLast = 0;
  private jetpack!: THREE.Group;
  private teamShirt!: THREE.Mesh;
  private parachute: THREE.Group | null = null;
  private cowboy: THREE.Group | null = null;
  private tauntKind = 0;
  private tauntStart = 0;
  private tauntUntil = 0;

  constructor(scene: THREE.Scene, cos: AvatarCosmetics = {}) {
    const bodyColor = cos.bodyColor ?? "#232733";
    const glowColor = cos.glowColor ?? "#00f0ff";
    this.zmTint = cos.bodyColor ?? "";
    const grad = toonGradient(); // still used by the cloth cosmetics (shirt, cowboy, canopy)
    // SURFACED, physically-shaded body (2026-08-01, `graphics`). Was a flat
    // MeshToonMaterial per part: one solid colour in 6 cel bands, no grain, no
    // varying specular — the last "toy" signal on the character after the
    // silhouette work. `avatarMaps` supplies a near-white albedo (so the
    // cosmetic bodyColor still tints exactly as before) plus a real normal and
    // roughness map, shared by every avatar in the room.
    const skinMat = new THREE.MeshStandardMaterial(
      avatarMaps("suit", { color: bodyColor, roughness: 0.88, metalness: 0.08 }));
    const darkMat = new THREE.MeshStandardMaterial(
      avatarMaps("gear", { color: "#15181f", roughness: 0.62, metalness: 0.22 }));
    // the hands get their OWN family — see avatarTextures.makeGlove
    const gloveMat = new THREE.MeshStandardMaterial(
      avatarMaps("glove", { color: "#1a1e27", roughness: 0.55, metalness: 0.18 }));

    // Full-body custom skin: one texture shared by every part, but each part's
    // box UVs are remapped onto an ASPECT-MATCHED region of the canvas so the
    // drawing lands without stretching (square head, wide torso, thin limbs).
    let skinTex: THREE.Texture | null = null;
    let skinMatShared: THREE.MeshStandardMaterial | null = null;
    if (cos.skinUrl) {
      skinTex = textureLoader.load(cos.skinUrl, (t) => { warmTexture(t); if (skinMatShared) skinMatShared.needsUpdate = true; });
      skinTex.colorSpace = THREE.SRGBColorSpace;
      // a drawn skin owns its own shading — no suit normal map over it (the
      // region UVs are an atlas crop, so a tiling detail map would smear)
      skinMatShared = new THREE.MeshStandardMaterial({ map: skinTex, color: "#ffffff", roughness: 0.85, metalness: 0.04 });
    }
    const applyRegionUV = (geo: THREE.BoxGeometry, r: SkinRegion): void => {
      const uv = geo.attributes.uv;
      for (let i = 0; i < uv.count; i++) {
        const u = uv.getX(i), v = uv.getY(i);
        uv.setXY(i, r.x + u * r.w, (1 - (r.y + r.h)) + v * r.h);
      }
      uv.needsUpdate = true;
    };

    // when a skin is set, `region` picks the aspect-matched atlas area + shared
    // skin material; otherwise falls back to the given solid-colour material
    const part = (
      w: number, h: number, d: number, mat: THREE.Material,
      round = 1, region?: SkinRegion, taper?: Taper, seg = 2,
    ): THREE.Mesh => {
      // Rounded, TAPERED, ELLIPTICAL body volumes — avatars read as figures,
      // not stacked cubes. applyRegionUV remaps whatever UVs exist, so custom
      // skins still land. Subdivision costs vertices, never draw calls
      // (seg 2 ≈ 300 tris/part, seg 3 ≈ 590; a full 8-bot lobby is ~35k tris,
      // well inside budget) — and it is what gives the elliptical cross-section
      // enough samples to shade as a curve instead of a facet.
      const r = Math.min(w, h, d) * 0.34;
      const geo = new RoundedBoxGeometry(w, h, d, seg, r) as unknown as THREE.BoxGeometry;
      if (region && skinMatShared) applyRegionUV(geo, region);
      else tileUV(geo, Math.max(0.7, Math.max(w, h, d) / TILE_M));
      roundCrossSection(geo, w, d, round);
      if (taper) taperGeometry(geo, h, taper.x, taper.z);
      const mesh = new THREE.Mesh(geo, region && skinMatShared ? skinMatShared : mat);
      // every extra object here is A DRAW CALL PER AVATAR PER FRAME — with 5
      // bots the outlines + glow edges alone were ~260 draws ("5 AI = lag").
      // Since 2026-08-01 a part is ONE draw: the inverted-hull black outline and
      // the glowing wireframe box that used to ride on top of it were the two
      // remaining comic-book tells, and a cel outline around a physically-shaded
      // body reads as a sticker. Removing them is both the look change and ~2
      // fewer draws per avatar.
      mesh.castShadow = w * h * d > 0.02;
      mesh.receiveShadow = true;
      return mesh;
    };
    // Humanoid taper profiles, [bottom, top]. These are the silhouette.
    const T_CHEST: Taper = { x: [0.80, 1.03], z: [0.90, 1.02] }; // ribcage -> shoulders
    const T_PELVIS: Taper = { x: [1.10, 0.86], z: [1.02, 0.92] }; // hips -> waist
    const T_HEAD: Taper = { x: [0.90, 1.0], z: [0.96, 1.02] };    // jaw -> cranium
    const T_ARM: Taper = { x: [0.66, 1.06], z: [0.70, 1.06] };   // wrist -> deltoid
    const T_THIGH: Taper = { x: [0.78, 1.04], z: [0.82, 1.02] }; // knee -> hip
    const T_SHIN: Taper = { x: [0.66, 1.04], z: [0.70, 1.04] };  // ankle -> calf

    // Proportions fit ~1.7 m so the visible head sits in the server's headshot
    // zone (top 25% of the 1.7 m hitbox = 1.28–1.70 m).
    // Shoulders WIDER than the old 0.58 and the waist narrower, so the torso
    // has a real V — the single biggest silhouette read at gameplay distance.
    const chest = part(0.62, 0.50, 0.35, skinMat, 0.68, SKIN_REGIONS.torso, T_CHEST, 3);
    this.chest = chest;
    chest.position.y = 1.16;
    const shirtGeo = new RoundedBoxGeometry(0.65, 0.53, 0.38, 2, 0.11);
    roundCrossSection(shirtGeo, 0.65, 0.38, 0.68); // follows the new ribcage, not a box around it
    taperGeometry(shirtGeo, 0.53, T_CHEST.x, T_CHEST.z);
    this.teamShirt = new THREE.Mesh(
      shirtGeo,
      new THREE.MeshToonMaterial({ color: "#ffffff", gradientMap: grad }),
    );
    this.teamShirt.position.y = 1.16;
    this.teamShirt.visible = false;
    // pelvis: wide at the hips, pinched at the waist where it meets the chest
    const waist = part(0.46, 0.31, 0.31, skinMat.clone(), 0.82, SKIN_REGIONS.torso, T_PELVIS);
    this.waist = waist;
    waist.position.y = 0.83;
    // NECK + TRAPEZIUS in one part: wide where it meets the shoulders, narrow
    // at the collar. This is the "merge the sub-volume INTO the part" trick the
    // BACKLOG asked for — a shoulder yoke for zero extra draw calls.
    const neck = part(0.25, 0.15, 0.22, darkMat, 1, undefined, { x: [1.22, 0.72], z: [1.16, 0.76] });
    neck.position.y = 1.40;

    // head: narrower jaw than cranium, elliptical section = an ovoid skull.
    // Top still lands at ~1.705 so the visible head stays inside the server's
    // headshot band (top 25% of 1.7 m).
    const head = part(0.35, 0.34, 0.35, skinMat.clone(), 0.92, SKIN_REGIONS.head, T_HEAD, 3);
    this.head = head;
    head.position.y = 1.535; // top ≈ 1.705
    // The visor/brow/chin sit on the FACE CENTRE LINE, which `roundCrossSection`
    // leaves at z = -d/2 exactly; they were narrowed here because the cranium's
    // corners now pull IN, so the old full-width bars would have hung in the air
    // at their ends.
    const visor = new THREE.Mesh(
      new THREE.BoxGeometry(0.22, 0.062, 0.02),
      new THREE.MeshBasicMaterial({ color: glowColor }),
    );
    visor.position.set(0, 1.56, -0.168); // face -z (game forward, same side as the gun)
    // brow/chin were the last two LIT parts on the model with no surface maps
    // at all (caught by agent/bench-gear-shape.mjs) — flat plastic next to a
    // normal-mapped helmet 3 cm away.
    const faceMat = new THREE.MeshStandardMaterial(
      avatarMaps("gear", { color: "#10141b", roughness: 0.5, metalness: 0.12 }));
    // brow + chin share one material and never move relative to each other, so
    // they are ONE mesh (2026-08-01): a draw call here is per-avatar per-frame,
    // and this pays for half of the arm's new elbow segment.
    const face = new THREE.Mesh(gearGeo([
      { g: new RoundedBoxGeometry(0.26, 0.042, 0.035, 1, 0.01), p: [0, 1.612, -0.162], tile: 1.4 },
      { g: new RoundedBoxGeometry(0.17, 0.042, 0.030, 1, 0.012), p: [0, 1.416, -0.160], tile: 1.4 },
    ]), faceMat);
    const cheek = (x: number): GearPiece => ({
      g: new RoundedBoxGeometry(0.070, 0.080, 0.040, 1, 0.014),
      p: [x, 1.500, -0.176], r: [0.05, 0, x < 0 ? -0.12 : 0.12], tile: 1.3,
    });
    const nose = new THREE.Mesh(gearGeo([
      { g: new RoundedBoxGeometry(0.062, 0.100, 0.060, 1, 0.014), p: [0, 1.515, -0.190], tile: 1.2 },
      cheek(-0.074),
      cheek(0.074),
    ]), skinMat.clone());

    // ---- ARMS: TWO BONES + a real HAND (2026-08-01, mission `graphics`).
    //
    // Until now an arm was ONE rigid segment with a rounded cap stuck on the
    // end — no elbow anywhere on the model, while the legs have had a knee and
    // an ankle since July. That is why the hold pose had to be eyeballed: with
    // a single bone the hand's position is whatever the shoulder angle happens
    // to put it at, so the gun was parked at a fixed body offset and the arms
    // were aimed *near* it. An elbow makes the hand REACHABLE, which is what
    // `solveArm` below then does exactly.
    //
    // Draw calls: upper + forearm + hand = 3 per arm where it used to be 2.
    // The +2 per avatar is paid back in the same commit by merging brow+chin
    // and the two pauldrons (see below) — net zero, measured on the cosmetics
    // scene.
    const makeArm = (x: number, side: number, region: SkinRegion): { shoulder: THREE.Group; elbow: THREE.Group } => {
      const shoulder = new THREE.Group();
      shoulder.position.set(x, SHOULDER_Y, 0);
      // A custom skin's arm region is split down its length between the two
      // bones (upper = top 55%, forearm = bottom 45%) instead of drawing the
      // whole arm twice, so a drawn sleeve still runs continuously (invariant 5).
      const upper = part(ARM_W, ARM_UPPER, ARM_W, skinMat.clone(), 1,
        subRegion(region, 0, 0.55), { x: [0.82, 1.06], z: [0.86, 1.06] });
      upper.position.y = -ARM_UPPER / 2;
      shoulder.add(upper);
      const elbow = new THREE.Group();
      elbow.position.y = -ARM_UPPER;
      // forearm: thick at the elbow, narrowing to a wrist — the taper is the
      // whole reason a bent arm reads as an arm and not as two pipes
      const fore = part(ARM_W * 0.9, ARM_FORE, ARM_W * 0.9, skinMat.clone(), 1,
        subRegion(region, 0.55, 1), { x: [0.62, 1.0], z: [0.66, 1.0] });
      fore.position.y = -ARM_FORE / 2;
      elbow.add(fore);
      const sleeve = new THREE.Mesh(armJointSleeveGeo(), darkMat);
      sleeve.castShadow = true;
      sleeve.receiveShadow = true;
      elbow.add(sleeve);
      const hand = new THREE.Mesh(handGeo(side), gloveMat);
      hand.position.y = -ARM_FORE - 0.008;
      hand.castShadow = true;
      elbow.add(hand);
      shoulder.add(elbow);
      return { shoulder, elbow };
    };
    // shoulders sit wider now, matching the fuller chest and hands.
    const aL = makeArm(-0.375, -1, SKIN_REGIONS.armL);
    const aR = makeArm(0.375, 1, SKIN_REGIONS.armR);
    this.armL = aL.shoulder; this.elbowL = aL.elbow;
    this.armR = aR.shoulder; this.elbowR = aR.elbow;

    // TWO-SEGMENT leg with a knee joint so crouch is a real bend, not a squash.
    // The thigh keeps its skin region (a plain box → applyRegionUV works); the
    // shin is a dark boot (no region), so skin mapping stays intact.
    const makeLeg = (x: number, region: SkinRegion): { hip: THREE.Group; knee: THREE.Group; ankle: THREE.Group } => {
      // thicker thigh, tapered to the knee; the shin keeps a calf above a
      // narrow ankle — a leg profile instead of two identical posts
      const hipY = 0.72, thigh = 0.43, shin = 0.34, w = 0.215;
      const hip = new THREE.Group();
      hip.position.set(x, hipY, 0);
      const thighBox = part(w, thigh, w, skinMat.clone(), 1, region, T_THIGH);
      thighBox.position.y = -thigh / 2;
      hip.add(thighBox);
      const knee = new THREE.Group();
      knee.position.y = -thigh;
      const shinBox = part(w * 0.94, shin, w * 0.94, darkMat, 1, undefined, T_SHIN);
      shinBox.position.y = -shin / 2;
      knee.add(shinBox);
      const gaiter = new THREE.Mesh(legJointSleeveGeo(w, shin), darkMat);
      gaiter.castShadow = true;
      gaiter.receiveShadow = true;
      knee.add(gaiter);
      // BOOT under its own ANKLE pivot, so the foot can roll heel→toe with the
      // gait instead of staying welded flat to the shin (see update()). The
      // pivot is a Group: no extra draw call, one extra matrix.
      const ankle = new THREE.Group();
      ankle.position.y = -shin;
      const foot = part(w * 1.04, 0.15, w * 1.95, darkMat, 0.64, undefined,
        { x: [0.9, 1.0], z: [1.0, 0.86] });
      foot.position.set(0, -0.01, -w * 0.42); // toes -z (forward)
      ankle.add(foot);
      const sole = part(w * 1.10, 0.048, w * 2.12, darkMat, 0.45, undefined,
        { x: [1.0, 0.96], z: [1.0, 0.97] });
      sole.position.set(0, -0.085, -w * 0.48);
      ankle.add(sole);
      knee.add(ankle);
      hip.add(knee);
      return { hip, knee, ankle };
    };
    // hips slightly wider apart, matching the widened pelvis
    const lL = makeLeg(-FOOT_HALF_STANCE, SKIN_REGIONS.legL);
    const lR = makeLeg(FOOT_HALF_STANCE, SKIN_REGIONS.legR);
    this.legL = lL.hip; this.kneeL = lL.knee; this.ankleL = lL.ankle;
    this.legR = lR.hip; this.kneeR = lR.knee; this.ankleR = lR.ankle;

    const torsoContinuity = new THREE.Mesh(torsoContinuityGeo(), darkMat);
    torsoContinuity.castShadow = true;
    torsoContinuity.receiveShadow = true;

    for (const fx of [face, nose]) fx.name = "npFX";
    this.group.add(chest, this.teamShirt, waist, torsoContinuity, neck, head, visor, face, nose, this.armL, this.armR, this.legL, this.legR);

    // ---- signature silhouette accents (glow-tinted, unmistakable at a glance) ----
    const glowMat = new THREE.MeshBasicMaterial({ color: glowColor });
    // HELMET SHELL. This replaces a 4-sided bright-cyan CONE stuck on top of the
    // skull — the single most toy-like thing on the model, and 24 cm of it stood
    // ABOVE the head, so the silhouette's tallest feature was a traffic cone.
    // A moulded half-dome that caps the cranium reads as gear, keeps the same
    // one draw call, and tops out below the head — still inside the server's
    // headshot band, like the head itself (invariant 5).
    const crest = new THREE.Mesh(
      new THREE.SphereGeometry(0.185, 16, 8, 0, Math.PI * 2, 0, Math.PI / 2),
      new THREE.MeshStandardMaterial(avatarMaps("gear", { color: "#1b1f29", roughness: 0.45, metalness: 0.35 })),
    );
    crest.scale.set(1, 0.62, 1.04);
    crest.position.set(0, 1.60, 0.005);
    // glowing chest reactor core
    const core = new THREE.Mesh(new THREE.SphereGeometry(0.09, 12, 12), glowMat);
    core.position.set(0, 1.16, -0.17);
    const coreGlow = new THREE.Mesh(new THREE.SphereGeometry(0.16, 12, 12),
      new THREE.MeshBasicMaterial({ color: glowColor, transparent: true, opacity: 0.22, blending: THREE.AdditiveBlending }));
    coreGlow.position.copy(core.position);
    // DELTOID caps — the old ones were flat angular slabs sitting on the
    // shoulder line; these are rounded, domed volumes that blend the arm into
    // the chest, so the shoulder joint stops reading as a cube socket.
    const pauldGeo = new RoundedBoxGeometry(0.23, 0.19, 0.33, 2, 0.09);
    roundCrossSection(pauldGeo, 0.23, 0.33, 1); // a shoulder cap is a dome, not a slab
    taperGeometry(pauldGeo, 0.20, [0.72, 1.0], [0.78, 1.0]);
    // The pair were TWO meshes with TWO material instances of the same family;
    // they are static relative to the body, so they merge into one draw
    // (2026-08-01 — the other half of the arm elbow's cost).
    const pauld = new THREE.Mesh(gearGeo([
      { g: pauldGeo.clone(), p: [-0.315, 1.33, 0], r: [0, 0, 0.315 * 0.75] },
      { g: pauldGeo.clone(), p: [0.315, 1.33, 0], r: [0, 0, -0.315 * 0.75] },
    ]), new THREE.MeshStandardMaterial(
      avatarMaps("gear", { color: "#20242e", roughness: 0.5, metalness: 0.3 })));
    pauldGeo.dispose();
    // accents are hero flair — the zombie horde drops them (LITE mode)
    for (const fx of [crest, core, coreGlow, pauld]) fx.name = "npFX";
    this.group.add(crest, core, coreGlow, pauld);

    // jetpack (on the back, +z) — hidden until the player picks one up
    this.jetpack = new THREE.Group();
    // Was ONE flat toon BOX with a glowing box lid on it. A jetpack is two
    // pressure tanks in a harness: twin capped cylinders, a ribbed spine plate
    // between them, and shoulder straps that actually run over the body — the
    // straps are what stop it reading as a rucksack-shaped decal (2026-08-01).
    const tank = (x: number): GearPiece[] => [
      { g: new THREE.CylinderGeometry(0.105, 0.105, 0.34, 16, 1), p: [x, 0.0, 0], tile: 2.2 },
      { g: new THREE.SphereGeometry(0.105, 16, 8, 0, Math.PI * 2, 0, Math.PI / 2), p: [x, 0.17, 0], s: [1, 0.8, 1], tile: 2.2 },
      { g: new THREE.SphereGeometry(0.105, 16, 8, 0, Math.PI * 2, 0, Math.PI / 2), p: [x, -0.17, 0], r: [Math.PI, 0, 0], s: [1, 0.8, 1], tile: 2.2 },
    ];
    const spine = new RoundedBoxGeometry(0.155, 0.44, 0.115, 2, 0.04);
    roundCrossSection(spine, 0.155, 0.115, 0.7);
    const jpBody = new THREE.Mesh(gearGeo([
      ...tank(-0.135), ...tank(0.135),
      { g: spine, p: [0, 0.02, -0.02], tile: 1.8 },
      // pressure manifold across the top of the two tanks
      { g: new THREE.CylinderGeometry(0.028, 0.028, 0.3, 10, 1), p: [0, 0.235, 0.0], r: [0, 0, Math.PI / 2], tile: 3 },
    ]), new THREE.MeshStandardMaterial(
      avatarMaps("plate", { color: "#3a6ea5", roughness: 0.42, metalness: 0.55 })));
    const jpTrim = new THREE.Mesh(new THREE.BoxGeometry(0.30, 0.055, 0.13), new THREE.MeshBasicMaterial({ color: glowColor }));
    jpTrim.position.y = 0.265;
    // twin thrust BEAMS: one VIOLET, one YELLOW — long additive cones that read
    // as a jet exhaust (esp. on the jetpack bots swarming the fleet).
    const beamCols = ["#b388ff", "#ffd24a"];
    let bi = 0;
    const hardware: GearPiece[] = [];
    for (const nx of [-0.135, 0.135]) {
      hardware.push(
        { g: new THREE.CylinderGeometry(0.055, 0.085, 0.14, 12, 1), p: [nx, -0.33, 0], tile: 2 },
        { g: new THREE.TorusGeometry(0.084, 0.012, 6, 14), p: [nx, -0.392, 0], r: [Math.PI / 2, 0, 0], tile: 3 },
      );
      const flame = new THREE.Mesh(new THREE.ConeGeometry(0.08, 0.5, 8), new THREE.MeshBasicMaterial({ color: beamCols[bi++], transparent: true, opacity: 0.8, blending: THREE.AdditiveBlending, depthWrite: false }));
      flame.rotation.x = Math.PI; flame.position.set(nx, -0.62, 0);
      this.jetpack.add(flame);
    }
    // SHOULDER HARNESS: two straps arcing forward over the deltoids, plus a
    // chest cinch. Merged with the nozzles — same rubber/webbing surface.
    // rotY(π/2) maps a torus's XY ring into the YZ plane, so its θ=0..π arc
    // runs FRONT → over the top → BACK: exactly the path a shoulder strap
    // takes. (The first cut arced in the wrong plane and sat inside the chest.)
    for (const sx of [-1, 1]) {
      hardware.push({
        g: new THREE.TorusGeometry(0.28, 0.026, 6, 20, Math.PI),
        p: [sx * 0.235, -0.02, -0.14], r: [0, Math.PI / 2, 0], s: [1, 0.86, 1], tile: 3.5,
      });
    }
    // chest cinch across the two straps, in front of the body
    hardware.push({ g: new THREE.CylinderGeometry(0.019, 0.019, 0.44, 8, 1), p: [0, -0.03, -0.42], r: [0, 0, Math.PI / 2], tile: 3 });
    this.jetpack.add(new THREE.Mesh(gearGeo(hardware), new THREE.MeshStandardMaterial(
      avatarMaps("gear", { color: "#15181f", roughness: 0.6, metalness: 0.25 }))));
    this.jetpack.add(jpBody, jpTrim);
    this.jetpack.position.set(0, 1.18, 0.28); // on the back
    this.jetpack.visible = false;
    this.group.add(this.jetpack);

    if (cos.headgear) {
      // A cosmetic CAP, not the 12 cm slab this used to be: a domed crown with
      // a stitched seam ring and a short peak, in one merged draw (2026-08-01).
      const hg = new THREE.Mesh(gearGeo([
        { g: new THREE.SphereGeometry(0.185, 20, 10, 0, Math.PI * 2, 0, Math.PI * 0.52), p: [0, 1.715, 0], s: [1, 0.72, 0.98], tile: 1.6 },
        { g: new THREE.TorusGeometry(0.184, 0.014, 6, 22), p: [0, 1.722, 0], r: [Math.PI / 2, 0, 0], s: [1, 0.98, 1], tile: 3 },
        // peak, angled down over the brow
        { g: new THREE.CylinderGeometry(0.185, 0.185, 0.022, 20, 1, false, Math.PI * 0.7, Math.PI * 0.6), p: [0, 1.722, -0.03], r: [-0.18, 0, 0], s: [1, 1, 1.5], tile: 2.4 },
      ]), new THREE.MeshStandardMaterial(
        avatarMaps("felt", { color: cos.headgear, roughness: 0.8, metalness: 0.05 })));
      hg.castShadow = true;
      this.group.add(hg);
    }

    scene.add(this.group);
    this.setWeapon("pistol");
    this.buildPlate();
  }

  /** FLAGS uniform: a solid team-colored shirt remains readable even over a
   * custom skin, unlike the nameplate-only team tint. */
  setTeamShirt(team: number): void {
    this.teamShirt.visible = team === 1 || team === 2;
    if (!this.teamShirt.visible) return;
    (this.teamShirt.material as THREE.MeshToonMaterial).color.set(team === 1 ? "#e9284f" : "#247cff");
  }

  private pirateGear: THREE.Group | null = null;
  /** PIRATE SHIP map: strap a tricorn hat + eyepatch on this avatar. */
  setPirateGear(on: boolean): void {
    if (on && !this.pirateGear) {
      const g = new THREE.Group();
      // felt hat, leather patch/cord, bone badge, gilded trim — real surfaces
      // now (2026-08-01). Before: three flat-coloured materials on 7 BOXES,
      // including a "brim" that was a 6 cm slab and a head "strap" that was a
      // hollow-less cube ring.
      const feltMat = new THREE.MeshStandardMaterial(
        avatarMaps("felt", { color: "#22222a", roughness: 0.95, metalness: 0.0 }));
      const hideMat = new THREE.MeshStandardMaterial(
        avatarMaps("leather", { color: "#1a1a20", roughness: 0.7, metalness: 0.05 }));
      const boneMat = new THREE.MeshStandardMaterial(
        avatarMaps("bone", { color: "#ded8c6", roughness: 0.62, metalness: 0.0 }));
      const goldMat = new THREE.MeshStandardMaterial(
        avatarMaps("plate", { color: "#c9a53a", roughness: 0.3, metalness: 0.8 }));

      // ---- TRICORN. A tricorn is named for its three COCKED corners; the old
      // one was a flat plate with a box on it. Brim = a disc whose edge sweeps
      // up in three places (`cockBrim`, lobes 3), crown = a tapered dome.
      const brim = new THREE.CylinderGeometry(0.44, 0.44, 0.022, 30, 1);
      cockBrim(brim, 0.44, 3, 0.16, Math.PI / 2); // one corner front, two rear
      const crown = new THREE.CylinderGeometry(0.175, 0.215, 0.20, 20, 1);
      const dome = new THREE.SphereGeometry(0.175, 20, 8, 0, Math.PI * 2, 0, Math.PI / 2);
      const hat = new THREE.Mesh(gearGeo([
        { g: brim, p: [0, 1.775, 0], s: [1, 1, 0.9], tile: 2.4 },
        { g: crown, p: [0, 1.875, 0], s: [1, 1, 0.92], tile: 1.4 },
        { g: dome, p: [0, 1.972, 0], s: [1, 0.55, 0.92], tile: 1.4 },
      ]), feltMat);
      // hatband: a real cord around the crown, not a painted stripe
      const band = new THREE.Mesh(gearGeo([
        { g: new THREE.TorusGeometry(0.208, 0.016, 6, 22), p: [0, 1.80, 0], r: [Math.PI / 2, 0, 0], s: [1, 0.92, 1], tile: 3 },
      ]), goldMat);

      // ---- SKULL AND CROSSBONES badge on the front of the crown.
      const badge = new THREE.Mesh(gearGeo([
        { g: new RoundedBoxGeometry(0.085, 0.075, 0.035, 2, 0.026), p: [0, 1.875, -0.185], r: [0, 0, 0], tile: 1 },
        { g: new RoundedBoxGeometry(0.028, 0.03, 0.03, 1, 0.012), p: [0, 1.833, -0.183], tile: 1 },   // jaw
        { g: new RoundedBoxGeometry(0.115, 0.016, 0.016, 1, 0.007), p: [0, 1.826, -0.176], r: [0, 0, 0.6], tile: 1 },
        { g: new RoundedBoxGeometry(0.115, 0.016, 0.016, 1, 0.007), p: [0, 1.826, -0.176], r: [0, 0, -0.6], tile: 1 },
      ]), boneMat);
      // sockets: two dark pits, unlit so they stay black at any light angle
      const sockets = new THREE.Mesh(gearGeo([
        { g: new THREE.SphereGeometry(0.014, 8, 6), p: [-0.021, 1.884, -0.203], s: [1, 1.2, 0.6] },
        { g: new THREE.SphereGeometry(0.014, 8, 6), p: [0.021, 1.884, -0.203], s: [1, 1.2, 0.6] },
      ]), new THREE.MeshBasicMaterial({ color: "#07070c" }));

      // ---- EYEPATCH: a domed leather lens over the left eye on a CORD that
      // actually passes round the skull (was a 2 cm cube and a box "band").
      const lens = new RoundedBoxGeometry(0.115, 0.105, 0.045, 2, 0.03);
      roundCrossSection(lens, 0.115, 0.045, 1); // an oval patch, not a tile
      const patch = new THREE.Mesh(gearGeo([
        { g: lens, p: [-0.078, 1.575, -0.155], r: [0, 0.18, 0.1], tile: 1 },
        { g: new THREE.TorusGeometry(0.183, 0.0075, 5, 24), p: [0, 1.615, 0.005], r: [Math.PI / 2 - 0.16, 0, 0.14], s: [1, 0.93, 1], tile: 4 },
      ]), hideMat);

      g.add(hat, band, badge, sockets, patch);
      g.traverse((o) => { o.name = "npFX"; }); // rides the LITE-mode toggle
      if (this.cosmeticsHidden) g.visible = false; // built while already lite → stay hidden
      this.pirateGear = g;
      this.group.add(g);
    } else if (!on && this.pirateGear) {
      this.group.remove(this.pirateGear);
      // materials are per-avatar (see the body parts' comment); the TEXTURES
      // they carry are module-shared and `material.dispose()` never touches them
      this.pirateGear.traverse((o) => {
        if (o instanceof THREE.Mesh) { o.geometry.dispose(); (o.material as THREE.Material).dispose(); }
      });
      this.pirateGear = null;
    }
  }

  /** 💀 HORDEBEARER skull face — worn while carrying the skull, so the
   *  bearer reads at a glance from any angle that shows the face. Same
   *  build-on-demand pattern as setPirateGear. NOT tagged npFX: knowing who
   *  commands the horde is gameplay, so LITE mode keeps it. */
  setSkullFace(on: boolean): void {
    if (on && !this.skullFace) {
      // Was a FLAT 4 cm bone SLAB with five dark boxes glued to its front — a
      // face painted on a plank. Now a curved cranial shell with a brow ridge,
      // cheekbones, a hinged jaw and real tooth pegs, in two merged draws
      // (bone + unlit hollows) instead of the old EIGHT.
      const bone = new THREE.MeshStandardMaterial(
        avatarMaps("bone", { color: "#e8e4d8", roughness: 0.58, metalness: 0.0 }));
      const dark = new THREE.MeshBasicMaterial({ color: "#0a0a12" });
      const g = new THREE.Group();
      // CRANIAL SHELL — the front half of an ovoid, wrapping the head (whose
      // front face sits at z≈-0.16, y 1.37-1.71) instead of standing off it.
      const shell = new THREE.SphereGeometry(0.185, 20, 14, Math.PI * 0.62, Math.PI * 0.76, 0, Math.PI * 0.62);
      const jaw = new RoundedBoxGeometry(0.20, 0.10, 0.15, 2, 0.045);
      roundCrossSection(jaw, 0.20, 0.15, 1);
      taperGeometry(jaw, 0.10, [0.78, 1.0], [0.8, 1.0]);
      g.add(new THREE.Mesh(gearGeo([
        { g: shell, p: [0, 1.585, -0.02], s: [1.0, 1.12, 0.92], tile: 1.6 },
        // brow ridge
        { g: new THREE.TorusGeometry(0.125, 0.022, 6, 16, Math.PI), p: [0, 1.615, -0.135], r: [0.35, 0, 0], s: [1, 0.55, 1], tile: 2.2 },
        // cheekbones
        { g: new THREE.SphereGeometry(0.05, 10, 8), p: [-0.098, 1.53, -0.125], s: [1, 0.7, 0.72], tile: 1.2 },
        { g: new THREE.SphereGeometry(0.05, 10, 8), p: [0.098, 1.53, -0.125], s: [1, 0.7, 0.72], tile: 1.2 },
        // jaw
        { g: jaw, p: [0, 1.435, -0.12], r: [0.12, 0, 0], tile: 1.4 },
        // upper + lower tooth pegs (this is what the old "tooth gaps" were
        // trying to imply with two dark boxes)
        ...[-0.06, -0.02, 0.02, 0.06].flatMap((x) => [
          { g: new THREE.BoxGeometry(0.024, 0.026, 0.02), p: [x, 1.4735, -0.185] as const, tile: 1 },
          { g: new THREE.BoxGeometry(0.024, 0.024, 0.02), p: [x, 1.4155, -0.187] as const, tile: 1 },
        ]),
      ]), bone));
      // sunken hollows — unlit so they read as holes from every light angle
      g.add(new THREE.Mesh(gearGeo([
        { g: new THREE.SphereGeometry(0.052, 12, 10), p: [-0.072, 1.585, -0.145], s: [1, 0.92, 0.55] },
        { g: new THREE.SphereGeometry(0.052, 12, 10), p: [0.072, 1.585, -0.145], s: [1, 0.92, 0.55] },
        { g: new THREE.SphereGeometry(0.028, 10, 8), p: [0, 1.505, -0.155], s: [1, 1.5, 0.6] },   // nasal aperture
        { g: new THREE.BoxGeometry(0.15, 0.022, 0.03), p: [0, 1.4445, -0.183] },                  // bite line
      ]), dark));
      this.skullFace = g;
      this.group.add(g);
    } else if (!on && this.skullFace) {
      this.group.remove(this.skullFace);
      this.skullFace.traverse((o) => {
        if (o instanceof THREE.Mesh) { o.geometry.dispose(); (o.material as THREE.Material).dispose(); }
      });
      this.skullFace = null;
    }
  }
  private skullFace: THREE.Group | null = null;

  private buildPlate(): void {
    const canvas = document.createElement("canvas");
    // taller than the old 72 to fit an XP line between the name and the bar
    canvas.width = 256; canvas.height = 92;
    this.plateCanvas = canvas;
    this.plateTex = new THREE.CanvasTexture(canvas);
    const mat = new THREE.SpriteMaterial({ map: this.plateTex, transparent: true, depthTest: true });
    this.plate = new THREE.Sprite(mat);
    this.plate.position.set(0, 2.2, 0);
    this.plate.scale.set(1.7, 0.61, 1);
    this.plate.renderOrder = 999;
    this.plate.visible = false; // shown once a name is set (match enemies only)
    this.group.add(this.plate);
    this.drawPlate();
  }

  private drawPlate(): void {
    const c = this.plateCanvas; if (!c) return;
    const g = c.getContext("2d")!;
    g.clearRect(0, 0, c.width, c.height);
    // name (prefixed with 📱 for phone players)
    g.font = "bold 30px 'Courier New', monospace";
    g.textAlign = "center"; g.textBaseline = "middle";
    g.lineWidth = 5; g.strokeStyle = "rgba(0,0,0,0.85)";
    const label = `${this.plateNemesis ? "⚔ " : ""}${this.plateBounty ? "👑 " : ""}${this.plateMobile ? "📱 " : ""}${this.plateName}`;
    g.strokeText(label, c.width / 2, 22);
    // BOUNTY targets glow gold; your NEMESIS burns red (only you see it)
    g.fillStyle = this.plateNemesis ? "#ff5566" : this.plateBounty ? "#ffd24a" : this.plateColor || "#ffffff";
    g.fillText(label, c.width / 2, 22);
    // XP readout (lifetime account XP) — only for real accounts (bots/guests = 0)
    if (this.plateXp > 0) {
      g.font = "bold 20px 'Courier New', monospace";
      const xpLabel = `${this.plateXp.toLocaleString("en-US")} XP`;
      g.lineWidth = 4; g.strokeStyle = "rgba(0,0,0,0.85)";
      g.strokeText(xpLabel, c.width / 2, 48);
      g.fillStyle = "#8fe3ff";
      g.fillText(xpLabel, c.width / 2, 48);
    }
    // health bar
    const bx = 38, by = 66, bw = c.width - 76, bh = 14;
    g.fillStyle = "rgba(0,0,0,0.75)"; g.fillRect(bx - 2, by - 2, bw + 4, bh + 4);
    const frac = Math.max(0, Math.min(1, this.plateHp));
    g.fillStyle = frac > 0.5 ? "#37d67a" : frac > 0.25 ? "#f4c542" : "#ff3355";
    g.fillRect(bx, by, bw * frac, bh);
    if (this.plateTex) this.plateTex.needsUpdate = true;
  }

  /** Set the enemy's displayed name (also reveals the nameplate). */
  private plateColor = "";
  private aaMark: THREE.Sprite | null = null;
  /** Show a "?" marker over the head when this player uses aim assist. */
  setAutoAimMarker(on: boolean): void {
    if (on && !this.aaMark) {
      const cvs = document.createElement("canvas"); cvs.width = 64; cvs.height = 64;
      const c = cvs.getContext("2d")!;
      c.font = "bold 52px monospace"; c.textAlign = "center"; c.textBaseline = "middle";
      c.fillStyle = "#ffcc33"; c.strokeStyle = "#000"; c.lineWidth = 5;
      c.strokeText("?", 32, 34); c.fillText("?", 32, 34);
      const tex = new THREE.CanvasTexture(cvs);
      this.aaMark = new THREE.Sprite(new THREE.SpriteMaterial({ map: tex, transparent: true, depthTest: false }));
      this.aaMark.scale.set(0.5, 0.5, 0.5);
      this.aaMark.position.set(0, 2.15, 0);
      this.aaMark.renderOrder = 999;
      this.group.add(this.aaMark);
    }
    if (this.aaMark) this.aaMark.visible = on;
  }

  private fuseMark: THREE.Sprite | null = null;
  /** BURNOUT: 🔥 over the head while this player's fuse is burning (top-rung
   *  overheat or a self-lit STOKE wager) — the walking bullseye everyone
   *  hunts. Same lazy-sprite pattern as the "?" aim-assist marker: built on
   *  first use, then visibility-toggled (an invisible sprite costs no draw). */
  setFuseMarker(on: boolean): void {
    if (on && !this.fuseMark) {
      const cvs = document.createElement("canvas"); cvs.width = 64; cvs.height = 64;
      const c = cvs.getContext("2d")!;
      c.font = "52px serif"; c.textAlign = "center"; c.textBaseline = "middle";
      c.fillText("🔥", 32, 36);
      const tex = new THREE.CanvasTexture(cvs);
      this.fuseMark = new THREE.Sprite(new THREE.SpriteMaterial({ map: tex, transparent: true, depthTest: false }));
      this.fuseMark.scale.set(0.55, 0.55, 0.55);
      this.fuseMark.position.set(0, 2.55, 0); // above the "?" slot — both can show
      this.fuseMark.renderOrder = 999;
      this.group.add(this.fuseMark);
    }
    if (this.fuseMark) this.fuseMark.visible = on;
  }

  private plateMobile = false;
  setName(name: string, color = ""): void {
    if (this.plate && name) this.plate.visible = true;
    if (name === this.plateName && color === this.plateColor) return;
    this.plateName = name;
    this.plateColor = color;
    this.schedulePlate();
  }

  /** 📱 badge on the nameplate — this player is on a phone. */
  setMobile(on: boolean): void {
    if (on === this.plateMobile) return;
    this.plateMobile = on;
    this.schedulePlate();
  }

  /** 👑 BOUNTY marker — this player is on a spree; the room should hunt them. */
  setBounty(on: boolean): void {
    if (on === this.plateBounty) return;
    this.plateBounty = on;
    this.schedulePlate();
  }
  private plateBounty = false;

  /** ⚔ NEMESIS marker — shown ONLY to the player this one keeps killing. */
  setNemesis(on: boolean): void {
    if (on === this.plateNemesis) return;
    this.plateNemesis = on;
    this.schedulePlate();
  }
  private plateNemesis = false;

  /** Set the nearby player's lifetime account XP shown on the plate (0 = hidden). */
  setXp(xp: number): void {
    const v = Math.max(0, Math.floor(xp || 0));
    if (v === this.plateXp) return;
    this.plateXp = v;
    this.schedulePlate();
  }

  /** Update the health bar (0..1). */
  setHealth(frac: number): void {
    const f = Math.max(0, Math.min(1, frac));
    if (Math.abs(f - this.plateHp) < 0.01) return;
    if (this.zm && f < this.plateHp) this.zm.hit(); // realistic zombie flinches
    this.plateHp = f;
    this.schedulePlate();
  }

  // Plate redraws are a canvas repaint + GPU texture upload EACH — an SMG
  // hosing a horde re-uploaded every hit zombie's plate every bullet.
  // Coalesce to ≤1 redraw / 120ms per avatar; the trailing timer guarantees
  // the FINAL value always lands.
  private plateNextAt = 0;
  private plateTimer = 0;
  private schedulePlate(): void {
    const now = performance.now();
    if (now >= this.plateNextAt) {
      this.plateNextAt = now + 120;
      this.drawPlate();
    } else if (!this.plateTimer) {
      this.plateTimer = window.setTimeout(() => {
        this.plateTimer = 0;
        this.plateNextAt = performance.now() + 120;
        this.drawPlate();
      }, Math.max(16, this.plateNextAt - now));
    }
  }

  /** Hide the nameplate (e.g. while the player is dead). */
  setPlateVisible(v: boolean): void {
    if (this.plate) this.plate.visible = v;
  }

  /** Remove the held weapon entirely (used by the mirror — the floating
   *  first-person gun is shown instead, so the avatar's held gun is redundant). */
  hideGun(): void {
    if (this.heldWeapon) { setCachedShown(this.heldWeapon, false); this.heldWeapon = null; }
    this.weaponId = "__none__";
  }

  /** Show/hide the jetpack on the character's back. */
  setJetpack(v: boolean): void {
    if (this.jetpack) this.jetpack.visible = v;
  }

  /** Show/hide a parachute canopy above the player (drop-in). */
  setParachute(v: boolean): void {
    if (!this.parachute) {
      this.parachute = new THREE.Group();
      // Was a smooth cel-shaded HEMISPHERE (a beach ball cut in half) on four
      // fat cords. A canopy is sewn from gores and vents at the apex: the
      // sphere now starts below the pole (an open vent hole), `goreCanopy`
      // bulges the fabric between 12 radial seams, and the ripstop weave comes
      // from the `nylon` family (2026-08-01).
      const canopyGeo = new THREE.SphereGeometry(1.6, 36, 10, 0, Math.PI * 2, 0.16, Math.PI / 2 - 0.16);
      goreCanopy(canopyGeo, 12, 0.045);
      tileUV(canopyGeo, 6);
      const canopy = new THREE.Mesh(canopyGeo, new THREE.MeshStandardMaterial(
        avatarMaps("nylon", { color: "#ff5a2a", roughness: 0.62, metalness: 0.0, side: THREE.DoubleSide })));
      canopy.position.y = 3.4;
      // 8 thin suspension lines converging on the harness, merged into ONE draw
      // (the old 4 were one draw each and as thick as a rope).
      const lines: GearPiece[] = [];
      for (let i = 0; i < 8; i++) {
        const a = (i / 8) * Math.PI * 2 + Math.PI / 8;
        const rx = Math.cos(a), rz = Math.sin(a);
        lines.push({
          g: new THREE.CylinderGeometry(0.009, 0.009, 2.5, 4),
          p: [rx * 0.62, 2.36, rz * 0.62], r: [rz * 0.24, 0, -rx * 0.24],
        });
      }
      this.parachute.add(new THREE.Mesh(gearGeo(lines), new THREE.MeshBasicMaterial({ color: "#1b1b22" })));
      this.parachute.add(canopy);
      this.group.add(this.parachute);
    }
    this.parachute.visible = v;
  }

  /** Wild West cosmetic overlay. It never replaces identity, skin, inventory,
   *  or held weapon; it is just hat/vest/bandana/holster geometry. */
  setCowboy(on: boolean): void {
    if (on && !this.cowboy) {
      this.cowboy = new THREE.Group();
      // Surfaced + reshaped 2026-08-01: this was four flat toon colours on a
      // FLAT-DISC brim, a plain cylinder crown, two slabs standing 5 cm off the
      // chest for a "vest", a slab across the front for a "belt" and two boxes
      // for holsters — 12 draw calls of stacked primitives. Now 4 merged draws.
      const leather = new THREE.MeshStandardMaterial(
        avatarMaps("leather", { color: "#4a2d1c", roughness: 0.68, metalness: 0.04 }));
      const felt = new THREE.MeshStandardMaterial(
        avatarMaps("felt", { color: "#7c4a28", roughness: 0.96, metalness: 0.0 }));
      const cloth = new THREE.MeshStandardMaterial(
        avatarMaps("suit", { color: "#8f2226", roughness: 0.9, metalness: 0.0 }));
      const brass = new THREE.MeshStandardMaterial(
        avatarMaps("plate", { color: "#d0a43a", roughness: 0.3, metalness: 0.85 }));

      // ---- CATTLEMAN HAT. `cockBrim` with 2 lobes rolls the two SIDES up and
      // leaves the front and back low — the shape everyone pictures. The crown
      // is domed and CREASED down the middle (the taper narrows its top, and
      // the dome is squashed in x), instead of a bare cylinder.
      const brim = new THREE.CylinderGeometry(0.43, 0.43, 0.026, 28, 1);
      cockBrim(brim, 0.43, 2, 0.135, 0);
      const crownGeo = new THREE.CylinderGeometry(0.185, 0.225, 0.24, 20, 1);
      const crownDome = new THREE.SphereGeometry(0.185, 20, 8, 0, Math.PI * 2, 0, Math.PI / 2);
      const hat = new THREE.Mesh(gearGeo([
        { g: brim, p: [0, 1.775, 0], s: [1, 1, 0.82], tile: 2.4 },
        { g: crownGeo, p: [0, 1.895, 0], s: [1, 1, 0.9], tile: 1.5 },
        { g: crownDome, p: [0, 2.012, 0], s: [0.86, 0.62, 0.9], tile: 1.5 },
      ]), felt);
      hat.castShadow = true;

      // ---- VEST that WRAPS the chest (open cylinder arcs at the ribcage
      // radius) + a belt that goes all the way round + hanging holsters with
      // flaps and hangers. All one leather buffer.
      // NB the ribcage is an ELLIPSE (hx 0.28, hz 0.155 after the 08-01 section
      // pass), so a circular arc at the chest's depth sinks inside the body at
      // its edges — the first cut of this vest was invisible except dead centre.
      // The arcs are scaled to x 0.30 / z 0.185, i.e. outside the chest at every
      // angle they cover.
      const vestArc = (side: number): THREE.BufferGeometry =>
        new THREE.CylinderGeometry(0.205, 0.19, 0.46, 12, 1, true,
          side < 0 ? Math.PI * 0.66 : Math.PI * 1.10, Math.PI * 0.4);
      const holster = (x: number): GearPiece[] => {
        const body = new RoundedBoxGeometry(0.105, 0.24, 0.095, 2, 0.035);
        roundCrossSection(body, 0.105, 0.095, 0.9);
        taperGeometry(body, 0.24, [0.72, 1.0], [0.78, 1.0]);
        const flap = new RoundedBoxGeometry(0.115, 0.075, 0.03, 1, 0.014);
        return [
          { g: body, p: [x, 0.70, -0.02], r: [0, 0, x * 0.5], tile: 1.2 },
          { g: flap, p: [x + x * 0.06, 0.815, -0.062], r: [0.5, 0, x * 0.5], tile: 1 },
          // hanger strap up to the belt
          { g: new THREE.BoxGeometry(0.055, 0.12, 0.03), p: [x * 0.95, 0.855, -0.01], r: [0, 0, x * 0.5], tile: 1 },
        ];
      };
      const vest = new THREE.Mesh(gearGeo([
        { g: vestArc(-1), p: [0, 1.13, 0], s: [1.46, 1, 0.9], tile: 2.6 },
        { g: vestArc(1), p: [0, 1.13, 0], s: [1.46, 1, 0.9], tile: 2.6 },
        // collar
        { g: new THREE.TorusGeometry(0.165, 0.03, 6, 20, Math.PI * 1.2), p: [0, 1.36, 0.01], r: [Math.PI / 2, 0, Math.PI * 0.9], s: [1.1, 0.85, 1], tile: 3 },
        // BELT: a band around the waist, not a plank across the front
        { g: new THREE.CylinderGeometry(0.225, 0.225, 0.075, 22, 1, true), p: [0, 0.855, 0], s: [1, 1, 0.82], tile: 3.2 },
        // hat band (leather, on the felt crown)
        { g: new THREE.TorusGeometry(0.218, 0.019, 6, 22), p: [0, 1.82, 0], r: [Math.PI / 2, 0, 0], s: [1, 0.9, 1], tile: 3 },
        ...holster(-0.30), ...holster(0.30),
      ]), leather);
      vest.castShadow = true;

      // ---- BUCKLE + hat-band concho, brass.
      const buckle = new THREE.Mesh(gearGeo([
        { g: new RoundedBoxGeometry(0.10, 0.075, 0.028, 2, 0.014), p: [0, 0.855, -0.192], tile: 1 },
        { g: new THREE.TorusGeometry(0.026, 0.008, 6, 14), p: [0, 1.82, -0.198], r: [0, 0, 0], tile: 1 },
      ]), brass);

      // ---- BANDANA: a knotted neckerchief that hangs from the neck instead of
      // a 3-sided cone floating at the chest.
      // (same ellipse problem as the vest — a 0.175 cone is inside the chest)
      const kerchief = new THREE.CylinderGeometry(0.085, 0.235, 0.24, 16, 1, true);
      const bandana = new THREE.Mesh(gearGeo([
        { g: kerchief, p: [0, 1.30, -0.035], r: [0.16, 0, 0], s: [1.3, 1, 0.8], tile: 2.2 },
        { g: new THREE.SphereGeometry(0.045, 10, 8), p: [0.075, 1.40, -0.13], s: [1, 0.85, 0.9], tile: 1.2 }, // knot
        { g: new RoundedBoxGeometry(0.06, 0.13, 0.03, 1, 0.014), p: [0.10, 1.33, -0.155], r: [0.2, 0, 0.35], tile: 1 }, // tail
      ]), cloth);

      this.cowboy.add(hat, vest, buckle, bandana);
      this.group.add(this.cowboy);
    }
    if (this.cowboy) this.cowboy.visible = on;
  }

  /** Trigger one of the short "moquerie" poses. */
  playTaunt(kind: number): void {
    this.tauntKind = Math.max(0, Math.min(4, Math.floor(kind)));
    this.tauntStart = performance.now();
    this.tauntUntil = this.tauntStart + 900;
  }

  /** Built held-weapon groups, kept attached (hidden) — every remote player/
   *  bot swaps per kill, so a swap is a visibility toggle, not a rebuild.
   *  Materials stay per-avatar (death fade / ghost opacity must not bleed
   *  across avatars); geometries are per-avatar CLONES of the module-wide partGeometryFor
   *  cache, merged by material (setWeapon) — never the cached objects. */
  private weaponCache = new Map<string, { group: THREE.Group; muzzleZ: number; foreZ: number }>();
  /** Where the LEFT hand supports the current weapon, in gun-local z (0 = the
   *  weapon is one-handed and the left arm swings free). */
  private foreZ = 0;

  /** Swap the third-person held weapon (a simple silhouette per class). */
  setWeapon(weaponId: string): void {
    if (weaponId === this.weaponId) return;
    this.weaponId = weaponId;
    if (this.heldWeapon) { setCachedShown(this.heldWeapon, false); this.heldWeapon = null; }
    if (weaponId === "none") {
      // unarmed (menu dais / previews): no gun, arms hang naturally
      return;
    }
    let entry = this.weaponCache.get(weaponId);
    if (!entry) {
      // the REAL weapon model — same per-weapon part list as the first-person
      // viewmodel (no more placeholder gun), scaled up for third-person reads.
      // The held weapon is RIGID (bakeStaticLocal below), so its parts never
      // move relative to one another: merge them by material family into one
      // mesh per bucket. Weapons author ~6-16 parts, and duplicate surface
      // families collapse — the ladder drops from 168 part-meshes to 117
      // (−30%; imperion 16→5, sniper 16→11, rifle/knife share nothing so 0),
      // measured in agent/bench-weaponmerge.mjs. Every remote player/bot on
      // screen holds one, so it is a persistent per-frame draw-call cut. Same
      // merge the vehicle body / critters / dragon already ship
      // (mergeVehicleBody, worldEntities.ts). castShadow=false is uniform across
      // the parts, so shadow flags don't need to be in the key.
      const { parts, muzzle } = partsFor(weaponId);
      const g = new THREE.Group();
      const SCALE = 1.35;
      // per-avatar geometry CLONES (buildWeaponMeshes never translates a
      // cached buffer) + per-avatar materials, one mesh per material bucket
      for (const m of buildWeaponMeshes(parts, { scale: SCALE, thirdPerson: true })) g.add(m);
      // The gun keeps its FIXED body offset — parenting it into the arm pivot
      // was tried and looked WORSE. What changed 2026-08-01 is the other
      // direction: the HANDS are now solved onto the gun (see solveArm), so the
      // fixed offset is the anchor for the whole pose instead of a thing the
      // arms happen to point at.
      g.position.set(GUN_X, GUN_Y, GUN_Z);
      bakeStaticLocal(g); // parts are static after build …
      g.matrixAutoUpdate = true; // … but the group itself follows aim pitch
      // FORE-END: where a support hand goes, at 55% of the way to the muzzle.
      // Short weapons (pistols, the knife) are held one-handed — a left hand
      // clamped onto a magnum's 12 cm slide looks worse than no hand at all.
      const barrel = -muzzle[2] * SCALE; // muzzle sits at -z; barrel > 0
      entry = {
        group: g,
        muzzleZ: GUN_Z + muzzle[2] * SCALE,
        foreZ: barrel > 0.34 ? -Math.min(0.42, barrel * 0.55) : 0,
      };
      g.userData.foreZ = entry.foreZ; // read by agent/bench-hands.mjs
      this.weaponCache.set(weaponId, entry);
      this.group.add(g);
    }
    setCachedShown(entry.group, true);
    this.muzzleLocal.set(GUN_X, GUN_Y, entry.muzzleZ);
    this.foreZ = entry.foreZ;
    this.heldWeapon = entry.group;
  }

  /** One shot fired — kick the weapon back and up. The hands ride the gun
   *  through the IK, so a burst visibly shakes the whole upper body instead of
   *  the gun sliding inside two static fists (2026-08-01, `graphics`). */
  recoil(strength = 1): void {
    this.recoilT = Math.min(1.4, this.recoilT + strength);
  }

  /** Muzzle position in world space (for remote gunfire FX). */
  muzzleWorld(out: THREE.Vector3): THREE.Vector3 {
    return this.group.localToWorld(out.copy(this.muzzleLocal));
  }

  /** Start the death animation (directional collapse + sink + fade). */
  die(): void {
    if (this.dying) return;
    this.dying = true;
    this.deathT = 0;
    this.deathLast = performance.now();
    // Pick a collapse direction — the old single canned +Z roll made every
    // death the same plank tipping onto its right side. Mostly backward or
    // sideways, occasionally a face-down slump. (+x pitch = onto the back:
    // the model faces -z.) Client-visual only, so Math.random is fine here.
    const r = Math.random();
    if (r < 0.34) { this.deathPitch = 1.35; this.deathRoll = (Math.random() - 0.5) * 0.5; }
    else if (r < 0.56) { this.deathPitch = 0.25; this.deathRoll = 1.45; }
    else if (r < 0.78) { this.deathPitch = 0.25; this.deathRoll = -1.45; }
    else { this.deathPitch = -1.42; this.deathRoll = (Math.random() - 0.5) * 0.4; }
    this.deathYawDrift = (Math.random() - 0.5) * 0.6;
    if (this.plate) this.plate.visible = false;
    this.zm?.startDeath(); // realistic zombie: the authored death take, no rigid roll
  }

  /** Reset from death (on respawn). */
  revive(): void {
    this.dying = false;
    this.deathT = 0;
    this.zm?.reset();
    this.zmSpeed = 0;
    this.group.rotation.set(0, 0, 0);
    this.group.scale.set(1, 1, 1);
    this.group.visible = this.dimVisible;
    this.setOpacity(1);
    if (this.plate && this.plateName) this.plate.visible = this.dimVisible;
  }

  private dimVisible = true;
  /** Hide the whole avatar when it's in a different dimension (practice mirror). */
  setDimVisible(v: boolean): void {
    if (this.dimVisible === v) return;
    this.dimVisible = v;
    if (!this.dying) { this.group.visible = v; if (this.plate) this.plate.visible = v && !!this.plateName; }
  }

  private immuneOn = false;
  private immuneOpacity = 0.4;
  /** Authored opacity/transparent, captured lazily the first time either the
   *  immune-ghost or the corpse-fade path touches a material. Restoring to
   *  "solid" then returns the neon accents to their DESIGNED translucency —
   *  edge lines 0.5 (:144), additive core-glow 0.22 (:261), jetpack flame 0.8
   *  (:289) — instead of clobbering them to a flat opaque 1. `setImmune` and
   *  `setOpacity` are the only code that mutates avatar opacity, and both
   *  capture before mutating, so whichever fires first records the pristine
   *  authored value; a lost shield / respawn no longer permanently flattens
   *  the glow. */
  private baseOpacityOf(m: THREE.Material & { opacity?: number }): { o: number; t: boolean } {
    const ud = m.userData as { npBaseO?: number; npBaseT?: boolean };
    if (ud.npBaseO === undefined) {
      ud.npBaseO = typeof m.opacity === "number" ? m.opacity : 1;
      ud.npBaseT = m.transparent;
    }
    return { o: ud.npBaseO, t: ud.npBaseT as boolean };
  }
  /** Ghost-translucency: spawn protection (0.4) or GHOST cloak (0.1) — so
   *  everyone can SEE protected/cloaked players are special. */
  setImmune(on: boolean, opacity = 0.4): void {
    if (this.immuneOn === on && (!on || this.immuneOpacity === opacity)) return;
    this.immuneOn = on;
    this.immuneOpacity = opacity;
    this.group.traverse((o) => {
      const m = (o as THREE.Mesh).material as (THREE.Material & { opacity?: number }) | undefined;
      if (m && o !== this.plate && typeof m.opacity === "number" && !m.userData.npNoFade) {
        const base = this.baseOpacityOf(m);
        m.transparent = on ? true : base.t;
        m.opacity = on ? opacity : base.o;
      }
    });
  }

  get isDying(): boolean { return this.dying; }

  private lastOpacity = -1;
  private setOpacity(o: number): void {
    // full-group traverse + material writes — skip when unchanged (persistent
    // PURGE corpses used to pay this EVERY frame at a constant 0.85)
    if (Math.abs(o - this.lastOpacity) < 0.004) return;
    this.lastOpacity = o;
    this.group.traverse((obj) => {
      const m = (obj as THREE.Mesh).material as THREE.Material | THREE.Material[] | undefined;
      if (!m) return;
      for (const mat of Array.isArray(m) ? m : [m]) {
        if (mat.userData.npNoFade) continue; // shared realistic-zombie materials never fade
        const base = this.baseOpacityOf(mat);
        // corpse/dying fade: uniform ghost opacity; back to solid: authored
        if (o < 1) { mat.transparent = true; mat.opacity = o; }
        else { mat.transparent = base.t; mat.opacity = base.o; }
      }
    });
  }

  /** Cheap per-frame pose write — position + facing ONLY, no limb animation.
   *  When a caller throttles the full `update()` (the deepest gfx ladder level
   *  still does, to shed the animation math on a Chromecast-class device) the
   *  body must STILL glide at frame rate: a lagging limb swing is invisible, a
   *  torso stepping at patch rate is the classic "laggy remote" look, and the
   *  universal practice is smooth position + limbs at animation rate. Mirrors
   *  update()'s NET transform: the crouch drop is held at its last animated
   *  value (correct — crouch eases slowly, so a frozen offset can't be seen). */
  glide(x: number, y: number, z: number, yaw: number): void {
    if (this.dying) return; // the death roll owns the transform; don't fight it
    let gy = y - this.crouchT * 0.34 + this.poseBobY;
    // Mirror update()'s ONE additive vertical transform: applyTauntPose adds a
    // sin bob (up to 8 cm) to position.y, and it runs only from update(). Without
    // matching it here, a taunting remote at the throttled gfx level snapped
    // between base (glide frames) and base+bob (update frames) — an ~8 cm
    // vertical judder at the update/render beat for the whole 900 ms taunt.
    if (this.tauntUntil > 0) {
      const now = performance.now();
      if (now < this.tauntUntil) {
        const t = Math.min(1, Math.max(0, (now - this.tauntStart) / 900));
        gy += Math.sin(t * Math.PI) * 0.08;
      }
    }
    this.group.position.set(x, gy, z);
    this.group.rotation.y = yaw;
    this.group.rotation.x = this.poseRotX;
    this.group.rotation.z = this.poseRotZ;
  }

  /**
   * Consume the foot plant detected by the last `update()`, if any.
   *
   * Returns 0 (nothing planted), -1 (LEFT foot) or +1 (RIGHT foot), and clears
   * the flag — one read per event, so a caller that skips frames still gets at
   * most one print per plant rather than a burst. `glide()` (the throttled
   * animation path) never advances the gait and therefore never reports a
   * footfall, which is correct: at that ladder level there are no legs to print.
   */
  takeFootfall(): number {
    const f = this.footPending;
    this.footPending = 0;
    return f;
  }

  /**
   * TWO-BONE IK — put the hand ON `target` (avatar-body space).
   *
   * Analytic, not iterative: the shoulder, elbow and target form a triangle
   * whose three sides are known (l1 = ARM_UPPER, l2 = ARM_LOWER, d = distance
   * to the target), so the law of cosines gives both joint angles directly and
   * the solve is ~30 flops with no allocation and no convergence loop.
   *
   *   - `bend` (elbow) = π − θ, with cos θ = (l1² + l2² − d²)/(2·l1·l2)
   *   - `alpha` (how far the UPPER arm leans off the line to the target),
   *     with cos α = (l1² + d² − l2²)/(2·l1·d)
   *
   * The chain is composed as  q_roll · q_aim · Rx(−α)  on the shoulder and
   * Rx(+bend) on the elbow. Both bones rest pointing down (0,−1,0), so:
   *  - q_aim (minimal rotation down → dir) points the whole chain at the target,
   *  - Rx(−α) tilts the upper bone off that line about the LOCAL x, and the
   *    elbow's +bend folds the forearm back onto it. Working the trigonometry
   *    through, the end effector lands at exactly d along dir — the law of sines
   *    (l1·sin α = l2·sin(bend−α)) is what closes the z term. bench-hands.mjs
   *    asserts the residual over a sweep of targets.
   *  - The bend plane therefore contains dir and the shoulder's local +z, i.e.
   *    the elbow drops BEHIND the arm — which is where a human elbow goes when
   *    the hands come forward. `roll` (about dir) flares it out or in.
   *
   * Out-of-range targets clamp d into [|l1−l2|, l1+l2] rather than failing, so
   * a stretched pose degrades into a straight arm pointing the right way.
   */
  private solveArm(shoulder: THREE.Object3D, elbow: THREE.Object3D, target: THREE.Vector3, roll: number): void {
    const l1 = ARM_UPPER, l2 = ARM_LOWER;
    _ikDir.subVectors(target, shoulder.position);
    const raw = _ikDir.length();
    const d = Math.min(l1 + l2 - 0.004, Math.max(Math.abs(l1 - l2) + 0.02, raw));
    if (raw < 1e-6) _ikDir.set(0, -1, 0); else _ikDir.multiplyScalar(1 / raw);
    const cb = Math.min(1, Math.max(-1, (l1 * l1 + l2 * l2 - d * d) / (2 * l1 * l2)));
    const bend = Math.PI - Math.acos(cb);
    const ca = Math.min(1, Math.max(-1, (l1 * l1 + d * d - l2 * l2) / (2 * l1 * d)));
    const alpha = Math.acos(ca);
    _ikQ.setFromUnitVectors(_IK_DOWN, _ikDir);
    if (roll !== 0) {
      _ikQRoll.setFromAxisAngle(_ikDir, roll);
      _ikQ.premultiply(_ikQRoll);
    }
    _ikQBend.setFromAxisAngle(_IK_X, -alpha);
    shoulder.quaternion.copy(_ikQ).multiply(_ikQBend);
    elbow.rotation.set(bend, 0, 0);
  }

  /**
   * Slide the SUPPORT-hand target back along the weapon's own axis until the
   * left arm can reach it, mutating `fore` in place.
   *
   * A rifle's handguard is ~0.45 m ahead of the grip and the shoulders are
   * 0.71 m apart, so on a wide-shouldered body the far end of a long weapon is
   * genuinely outside the left arm's 0.605 m sphere. Clamping inside `solveArm`
   * would fix the arm but leave the hand hanging in space beside the gun; this
   * keeps the hand ON THE WEAPON and just chooses a nearer point on the
   * handguard, which is what a person does.
   *
   * Line/sphere: with A = grip, d = fore − A, r = A − shoulder, find the
   * smaller root of |r + s·d|² = R², clamped to the segment.
   */
  private reachableFore(shoulder: THREE.Vector3, grip: THREE.Vector3, fore: THREE.Vector3): void {
    const R = (ARM_UPPER + ARM_LOWER) * 0.985;
    if (fore.distanceToSquared(shoulder) <= R * R) return;
    _ikSeg.subVectors(fore, grip);
    _ikRel.subVectors(grip, shoulder);
    const a = _ikSeg.lengthSq();
    if (a < 1e-9) return;
    const b = _ikSeg.dot(_ikRel);
    const c = _ikRel.lengthSq() - R * R;
    const disc = b * b - a * c;
    // disc < 0 ⇒ even the grip is out of reach (never happens with the shipped
    // proportions, but a cosmetic body scale could): fall back to the grip and
    // let solveArm's clamp handle it.
    const s = disc < 0 ? 0 : Math.min(1, Math.max(0, (-b + Math.sqrt(disc)) / a));
    fore.copy(grip).addScaledVector(_ikSeg, s);
  }

  /** pos = feet position. Walk cycle derived from horizontal displacement. */
  update(x: number, y: number, z: number, yaw: number, crouching: boolean, pitch = 0): void {
    if (this.zm) { this.updateZombieModel(x, y, z, yaw); return; }
    if (this.dying) {
      const now = performance.now();
      this.deathT += (now - this.deathLast) / 1000;
      this.deathLast = now;
      const fall = Math.min(1, this.deathT / 0.55);
      const e = fall * fall; // ease-in — a falling body ACCELERATES, then stops on impact
      this.group.position.set(x, y - e * 0.1, z);
      this.group.rotation.set(this.deathPitch * e, yaw + this.deathYawDrift * e, this.deathRoll * e);
      this.group.scale.set(1, 1, 1);
      // the body CRUMPLES as it goes over — knees buckle, arms flop, head lolls
      // (the old death held the standing pose rigid all the way down)
      this.legL.rotation.x = 0.35 * e;
      this.legR.rotation.x = 0.6 * e;
      if (this.kneeL) this.kneeL.rotation.x = -1.1 * e;
      if (this.kneeR) this.kneeR.rotation.x = -0.65 * e;
      this.armL.rotation.set(0.3 * e, 0, 0.55 * e);
      this.armR.rotation.set(-0.2 * e, 0, -0.6 * e);
      this.elbowL.rotation.set(0.35 * e, 0, 0);
      this.elbowR.rotation.set(0.25 * e, 0, 0);
      this.head.rotation.set(-0.3 * e, 0.2 * e, 0.15 * e);
      this.chest.rotation.set(0.18 * e, 0, 0.08 * e);
      if (this.corpsePersist) {
        this.setOpacity(0.85); // PURGE team play: the body STAYS — walk up and revive
        return;
      }
      this.setOpacity(Math.max(0, 1 - Math.max(0, (this.deathT - 0.7) / 0.5))); // fade after 0.7s
      if (this.deathT > 1.25) this.group.visible = false; // fully gone
      return;
    }
    this.group.position.set(x, y, z);
    this.group.rotation.y = yaw;
    this.group.scale.y = 1; // no more full-body squash — real knee bend below

    // advance walk phase by horizontal distance moved (frozen while airborne —
    // legs cycling mid-air was the single biggest "video game" tell in a jump)
    let stepDist = 0;
    let dxW = 0, dzW = 0, dyW = 0;
    if (this.hasLast) {
      dxW = x - this.lastPos.x;
      dzW = z - this.lastPos.z;
      dyW = y - this.lastPos.y;
      stepDist = Math.hypot(dxW, dzW);
      this.walkPhase += stepDist * 3.2 * (1 - this.airT);
    }
    this.lastPos.set(x, y, z);
    this.hasLast = true;

    // Ease the crouch so it bends/unbends smoothly. This was a flat `* 0.25`
    // PER CALL, so the squat took 4x longer in wall-clock terms at 60fps than
    // at 240fps — the same frame-rate dependence invariant 3 exists to stop,
    // just in a cosmetic smoother rather than in `kin`. Exponential form is
    // rate-matched to the old constant at 60fps (0.25/frame => ~17.3/s), so
    // 60Hz looks exactly as it did and every other refresh rate now matches it.
    const nowMs = performance.now();
    const dt = this.crouchLastMs > 0 ? Math.min(0.1, (nowMs - this.crouchLastMs) / 1000) : 1 / 60;
    this.crouchLastMs = nowMs;
    this.crouchT += ((crouching ? 1 : 0) - this.crouchT) * (1 - Math.exp(-dt * 17.3));
    const cr = this.crouchT;
    // ---- vertical state: airborne detection + landing compression ----------
    const invDt = 1 / Math.max(dt, 1 / 240);
    const vyRaw = dyW * invDt;
    if (Math.abs(vyRaw) > 30) {
      // teleport / respawn snap — don't read it as a 200 m/s fall
      this.vyS = 0; this.fallPeak = 0; this.airWas = false;
    } else {
      this.vyS += (vyRaw - this.vyS) * (1 - Math.exp(-dt * 14));
    }
    // 2.2 m/s clears elevator/platform rides; a real jump leaves at ~7 m/s
    const airborne = Math.abs(this.vyS) > 2.2;
    if (airborne) this.fallPeak = Math.min(this.fallPeak, vyRaw);
    if (this.airWas && !airborne && this.fallPeak < -3) {
      this.landK = Math.min(1, this.landK + Math.min(1, -this.fallPeak / 12));
    }
    if (!airborne) this.fallPeak = 0;
    this.airWas = airborne;
    this.airT += ((airborne ? 1 : 0) - this.airT) * (1 - Math.exp(-dt * (airborne ? 10 : 13)));
    this.landK *= Math.exp(-dt * 6.5);
    if (this.landK < 0.003) this.landK = 0;
    const air = this.airT, land = this.landK;
    // ---- local-space velocity: strafe/backpedal awareness ------------------
    const sy = Math.sin(yaw), cy = Math.cos(yaw);
    const fwdV = Math.max(-12, Math.min(12, -(dxW * sy + dzW * cy) * invDt));
    const latV = Math.max(-12, Math.min(12, (dxW * cy - dzW * sy) * invDt));
    this.fwdS += (fwdV - this.fwdS) * (1 - Math.exp(-dt * 8));
    this.latS += (latV - this.latS) * (1 - Math.exp(-dt * 8));
    const leanDir = Math.max(-1, Math.min(1, this.fwdS / 6)); // + running forward, - backpedal
    const sideLean = Math.max(-1, Math.min(1, this.latS / 8.5)) * 0.13 * (1 - air * 0.5);
    const rawStride = Math.min(1.35, (stepDist / Math.max(dt, 1 / 120)) / 8.5);
    this.strideT += (rawStride - this.strideT) * (1 - Math.exp(-dt * (rawStride > this.strideT ? 10 : 6)));
    const stride = this.strideT * (1 - cr * 0.55);
    // FOOTFALL (2026-08-01, `graphics`): the gait now reports its own foot
    // plants, so the world decals under a walker come from the ANIMATION rather
    // than from a parallel timer that drifts against it. `legL.rotation.x` is
    // +sin(walkPhase) and a positive rotation carries the hanging limb FORWARD,
    // so the left leg reaches full extension at walkPhase = π/2 + 2kπ — that is
    // heel strike. Half-cycle index k = floor((phase - π/2)/π) therefore ticks
    // exactly once per plant, and its parity says which foot: even = left.
    // walkPhase only ever increases (it integrates |displacement|), so this
    // cannot double-fire, and gating on `strideT` keeps a standing avatar from
    // stamping prints as it shuffles a few millimetres.
    const half = Math.floor((this.walkPhase - Math.PI / 2) / Math.PI);
    if (this.footHalfInit && half !== this.footHalf && this.strideT > 0.22) {
      this.footPending = (((half % 2) + 2) % 2) === 0 ? -1 : 1;
    }
    this.footHalf = half;
    this.footHalfInit = true;
    const swing = Math.sin(this.walkPhase) * (0.55 + stride * 0.24) * (1 - cr * 0.7) * (1 - air * 0.85);
    const step = Math.sin(this.walkPhase);
    const step2 = Math.sin(this.walkPhase * 2);
    const lean = Math.min(0.22, stride * 0.15) * (1 - cr * 0.35);
    const sway = Math.sin(this.walkPhase * 0.5) * stride * (1 - air);
    const breathing = Math.sin(nowMs * 0.0022) * (0.014 + 0.008 * (1 - Math.min(1, stride)));
    const shoulderRoll = step * 0.055 * stride;
    // airborne: lean back while rising, forward while falling (vy-proportional)
    const airLean = air * Math.max(-0.15, Math.min(0.2, -this.vyS * 0.022));
    this.poseBobY = Math.abs(Math.cos(this.walkPhase)) * 0.045 * stride * (1 - air) + breathing * 0.35 - cr * 0.02 - land * 0.17;
    // lean FOLLOWS the movement direction now: forward run leans forward,
    // backpedal leans back, strafing rolls the body into the step while the
    // head counter-tilts to stay level (below)
    this.poseRotX = -lean * 0.35 * leanDir + airLean;
    this.poseRotZ = sway * 0.022 - sideLean;
    this.chest.rotation.x = -lean * leanDir + cr * 0.18 + breathing + land * 0.3 + airLean * 0.8;
    this.chest.rotation.y = step2 * 0.018 * stride;
    this.chest.rotation.z = sway * 0.055 + shoulderRoll - sideLean * 0.6;
    this.waist.rotation.x = cr * 0.12 - breathing * 0.35;
    this.waist.rotation.y = -step2 * 0.014 * stride;
    this.waist.rotation.z = -sway * 0.04;
    this.head.rotation.x = pitch * 0.25 - lean * 0.35 * leanDir - breathing * 0.25 + land * 0.12;
    this.head.rotation.y = -step2 * 0.012 * stride;
    this.head.rotation.z = sway * 0.03 - shoulderRoll * 0.25 + sideLean * 0.45;
    // crouch = thighs forward + knees folded back + torso sinks (a real squat);
    // airborne = asymmetric tuck (lead leg up, trail leg back);
    // landing = both knees compress and the body dips for a beat
    this.legL.rotation.x = swing + cr * 0.95 + air * 0.52 + land * 0.45;
    this.legR.rotation.x = -swing + cr * 0.95 - air * 0.26 + land * 0.45;
    // GAIT (2026-07-31, `graphics`): the knees used to move ONLY on crouch, so
    // walking was two dead-straight pendulums pivoting at the hip — the single
    // most robotic thing about the old locomotion. A real leg flexes hard while
    // it swings THROUGH and straightens as the heel plants. `swing` is
    // sin(phase) for the left leg and its negation for the right, so
    // max(0, -swing) is exactly "this leg is travelling forward"; the knee
    // folds back (negative x) over that half of the cycle, scaled by stride so
    // a standing avatar keeps its legs straight.
    const flexL = Math.max(0, -swing) * 0.95 * stride;
    const flexR = Math.max(0, swing) * 0.95 * stride;
    const kL = -cr * 1.7 - flexL - air * 1.0 - land * 0.85;
    const kR = -cr * 1.7 - flexR - air * 0.5 - land * 0.85;
    if (this.kneeL) this.kneeL.rotation.x = kL;
    if (this.kneeR) this.kneeR.rotation.x = kR;
    // ANKLE ROLL (2026-08-01, `graphics`): the boot used to be welded to the
    // shin, so a walking avatar planted a rigid flat plank at every step and
    // pointed its toes at the sky whenever the knee bent — the last robotic
    // tell left in the gait after the knee flex. Counter-rotating 80% of the
    // hip+knee pitch keeps the sole roughly level with the ground (a real ankle
    // does exactly this), and the `swing` term adds the heel-strike/toe-off
    // roll: +x lifts the toe (the toes point -z, and R_x(a)·(0,0,-1) has
    // y = sin a), and swing > 0 is this leg travelling FORWARD.
    // the -air term points the toes DOWN mid-jump (+x lifts the toe)
    if (this.ankleL) this.ankleL.rotation.x = -(this.legL.rotation.x + kL) * 0.8 + swing * 0.24 * stride - air * 0.28;
    if (this.ankleR) this.ankleR.rotation.x = -(this.legR.rotation.x + kR) * 0.8 - swing * 0.24 * stride - air * 0.28;
    // drop the whole body so the folded legs keep the feet near the ground
    this.group.position.y = y - cr * 0.34 + this.poseBobY;
    if (this.zombie) {
      // classic shambler: BOTH arms dead-straight forward (+x rotates the
      // hanging limb toward the model's front), a lurching side sway, and a
      // lolling head tilt
      this.armL.rotation.set(1.45 + swing * 0.14, 0, 0.16);
      this.armR.rotation.set(1.6 - swing * 0.14, 0, -0.1); // asymmetric reach — more broken
      // elbows barely break — the shambler's reach is stiff, and a dead-straight
      // arm is the pose the two-bone chain has to be told to hold
      this.elbowL.rotation.set(0.12 + swing * 0.05, 0, 0);
      this.elbowR.rotation.set(0.20 - swing * 0.05, 0, 0);
      this.poseRotX = 0.16;
      this.poseRotZ = Math.sin(this.walkPhase * 0.5) * 0.07; // lurch
      this.group.rotation.x = this.poseRotX; // hunched forward
      this.group.rotation.z = this.poseRotZ;
      this.head.rotation.z = 0.18 + Math.sin(this.walkPhase * 0.5) * 0.06; // lolling head
      this.applyTauntPose(nowMs, pitch); // reuse this frame's clock (no 2nd performance.now)
      return;
    }
    this.group.rotation.x = this.poseRotX;
    this.group.rotation.z = this.poseRotZ;
    if (this.heldWeapon) {
      // THE HOLD, rebuilt 2026-08-01. The old pose set four eyeballed shoulder
      // Euler angles and hoped the (single-bone) arms ended up near a gun that
      // was parked at a fixed offset — at any aim pitch other than the one it
      // was tuned at, the hands drifted off it. Now the GUN is the anchor and
      // both hands are SOLVED onto it, so the grip is held at every pitch, in
      // every stride phase, and through the recoil kick for free.
      const w = this.heldWeapon;
      // recoil: the weapon rides back (+z) and rotates muzzle-up, decaying
      // rate-independently like the crouch ease
      this.recoilT *= Math.exp(-dt * 9);
      if (this.recoilT < 0.002) this.recoilT = 0;
      const kick = this.recoilT;
      w.position.set(GUN_X, GUN_Y + kick * 0.014 + breathing * 0.5, GUN_Z + kick * 0.06);
      w.rotation.set(
        pitch - lean * 0.25 - kick * 0.34,
        -0.13 - step2 * 0.02 * stride,           // canted across the body
        0.07 - shoulderRoll * 0.35 + sway * 0.04,
      );
      // RIGHT hand on the trigger grip …
      _ikTarget.set(0, GRIP_LOCAL_Y, GRIP_LOCAL_Z).applyEuler(w.rotation).add(w.position);
      this.solveArm(this.armR, this.elbowR, _ikTarget, -0.30 + shoulderRoll);
      if (this.foreZ < 0) {
        // … LEFT hand on the handguard, pulled back along the weapon's own axis
        // to the farthest point the left arm can actually reach (see
        // `reachableFore`) so the support hand never floats off the gun.
        // Both ends of that segment sit on the HANDGUARD line (same local y/z
        // offsets, different z) — sliding toward the trigger grip instead would
        // walk the hand off the weapon's axis as it retracted.
        _ikGrip.set(0, GRIP_LOCAL_Y * 0.3, GRIP_LOCAL_Z).applyEuler(w.rotation).add(w.position);
        _ikTarget.set(0, GRIP_LOCAL_Y * 0.3, this.foreZ).applyEuler(w.rotation).add(w.position);
        this.reachableFore(this.armL.position, _ikGrip, _ikTarget);
        this.solveArm(this.armL, this.elbowL, _ikTarget, 0.34 - shoulderRoll);
      } else {
        // one-handed (pistols, the knife): the free arm swings with the gait,
        // elbow softly bent — a dead-straight swinging arm is the robot tell.
        // Airborne it reaches out for balance.
        this.armL.rotation.set(-swing * 0.7 - 0.12 + air * 0.32, 0, (0.16 + shoulderRoll * 0.55) * (1 + air * 2.2));
        this.elbowL.rotation.set(0.42 + Math.max(0, swing) * 0.5 + air * 0.2, 0, 0);
      }
    } else {
      // unarmed: arms swing along the body (the menu-dais look), and the elbows
      // break on the FORWARD half of each swing the way a real arm does.
      // Airborne both arms come slightly forward and flare out for balance
      // (scaling the authored ±0.08 hang keeps the flare direction correct).
      this.armL.rotation.set(-swing * 0.82 + air * 0.32, 0, (shoulderRoll * 0.55 + 0.08) * (1 + air * 3.5));
      this.armR.rotation.set(swing * 0.82 + air * 0.32, 0, (-shoulderRoll * 0.55 - 0.08) * (1 + air * 3.5));
      this.elbowL.rotation.set(0.22 + Math.max(0, -swing) * 0.85 + air * 0.2, 0, 0);
      this.elbowR.rotation.set(0.22 + Math.max(0, swing) * 0.85 + air * 0.2, 0, 0);
    }
    this.applyTauntPose(nowMs, pitch); // reuse this frame's clock (no 2nd performance.now)
  }

  private applyTauntPose(now: number, pitch: number): void {
    if (now >= this.tauntUntil) return;
    const t = Math.min(1, Math.max(0, (now - this.tauntStart) / 900));
    const pulse = Math.sin(t * Math.PI);
    const wave = Math.sin(t * Math.PI * 8);
    this.group.position.y += pulse * 0.08;
    // taunts author shoulder angles only; give the (new) elbows a pose each so
    // a taunting avatar isn't waving two straight planks
    const elbow = this.tauntKind === 2 ? 2.1 : this.tauntKind === 0 ? 1.35 : 0.55;
    this.elbowL.rotation.set(elbow, 0, 0);
    this.elbowR.rotation.set(this.tauntKind === 3 ? 1.2 : elbow, 0, 0);
    // (positive x = arm toward the model's FRONT — same convention note as
    // the hold pose above; these were authored with the inverted sign)
    switch (this.tauntKind) {
      case 0: // wave-off
        this.armL.rotation.set(2.35 + wave * 0.22, 0, -0.95);
        this.armR.rotation.set(0.3 + pitch * 0.2, 0, 0.3);
        break;
      case 1: // shrug
        this.armL.rotation.set(0.45, 0, -1.15 + wave * 0.08);
        this.armR.rotation.set(0.45, 0, 1.15 - wave * 0.08);
        break;
      case 2: // flex
        this.armL.rotation.set(1.55, 0, -1.35);
        this.armR.rotation.set(1.55, 0, 1.35);
        break;
      case 3: // point and laugh
        this.armL.rotation.set(0.35 + wave * 0.08, 0, -0.25);
        this.armR.rotation.set(1.75, 0, -0.75);
        break;
      default: // little dance
        this.armL.rotation.set(0.8 + wave * 0.3, 0, -1.45);
        this.armR.rotation.set(0.8 - wave * 0.3, 0, 1.45);
        this.legL.rotation.x += wave * 0.25;
        this.legR.rotation.x -= wave * 0.25;
        break;
    }
  }

  dispose(scene: THREE.Scene): void {
    window.clearTimeout(this.plateTimer); // pending plate redraw dies with us
    this.plateTimer = 0;
    scene.remove(this.group);
    // realistic zombie first: its geometry/materials/textures are SHARED and
    // disposeDeep would free the materials — detach it and free only the
    // per-instance skeleton + mixer (the stash stays and is deep-freed below)
    if (this.zm) { this.zm.dispose(); this.zm = null; }
    // deep-dispose: avatars are torn down mid-match (player leave, menu dais,
    // cosmetic preview clicks) — scene.remove alone leaked ~30 geometries +
    // toon materials + the nameplate canvas texture per teardown
    disposeDeep(this.group);
  }
}
